<template>
  <div class="payment-purchase-page bg-white shadow-sm border border-slate-200">
    <div class="payment-purchase-section-pad pt-2 pb-0.5">
      <div class="flex items-baseline gap-2 flex-wrap">
        <h2 class="text-base font-bold text-[#0d4169] text-left">
          {{ resolvedCompanyName }}
        </h2>
        <span class="text-sm font-bold text-slate-600">Payment / Purchase</span>
      </div>
    </div>

    <form class="payment-purchase-section-pad pt-2 pb-4 payment-purchase-form" @submit.prevent="onGenerate">
      <div class="payment-purchase-toolbar">
        <div class="payment-purchase-toolbar__field payment-purchase-toolbar__field--grv">
          <label class="payment-purchase-toolbar__label">GRV Number</label>
          <select
            v-model="draft.grvNumber"
            class="payment-purchase-toolbar__control payment-purchase-toolbar__control--select"
            :disabled="loadingAvailableGrvNumbers"
            @change="onGrvNumberInput"
          >
            <option value="">Select GRV Number</option>
            <option
              v-for="no in grvNumberOptions"
              :key="`grv-${no}`"
              :value="no"
            >
              {{ no }}
            </option>
          </select>
        </div>

        <div class="payment-purchase-toolbar__field payment-purchase-toolbar__field--ns">
          <label class="payment-purchase-toolbar__label">Non-Stockable Number</label>
          <select
            v-model="draft.nonStockableNumber"
            class="payment-purchase-toolbar__control payment-purchase-toolbar__control--select"
            :disabled="loadingAvailableNsNumbers"
            @change="onNsNumberInput"
          >
            <option value="">Select NS Number</option>
            <option
              v-for="no in nsNumberOptions"
              :key="`ns-${no}`"
              :value="no"
            >
              {{ no }}
            </option>
          </select>
        </div>

        <div class="payment-purchase-toolbar__field payment-purchase-toolbar__field--other">
          <label class="payment-purchase-toolbar__label">Other</label>
          <button
            type="button"
            class="doc-toggle-btn payment-purchase-toolbar__btn payment-purchase-toolbar__btn--other"
            :class="{ 'payment-purchase-toolbar__btn--other-active': otherModeActive }"
            @click="toggleOtherMode"
          >
            Other
          </button>
        </div>

        <div class="payment-purchase-toolbar__field payment-purchase-toolbar__field--payment-type">
          <label class="payment-purchase-toolbar__label">C.P.V or P.C.P.V</label>
          <select
            v-model="draft.paymentType"
            class="payment-purchase-toolbar__control payment-purchase-toolbar__control--select"
            :disabled="isPcpvSessionLocked"
          >
            <option value="" disabled>Payment type</option>
            <option
              v-for="opt in paymentTypeOptions"
              :key="opt"
              :value="opt"
            >
              {{ opt }}
            </option>
          </select>
        </div>

        <div class="payment-purchase-toolbar__field payment-purchase-toolbar__field--generate">
          <span class="payment-purchase-toolbar__label payment-purchase-toolbar__label--spacer" aria-hidden="true">Generate</span>
          <button
            type="submit"
            :disabled="isLoading"
            class="payment-purchase-toolbar__btn payment-purchase-toolbar__btn--generate"
          >
            {{ isLoading ? 'Loading...' : 'Generate' }}
          </button>
        </div>
      </div>
    </form>

    <!-- P.C.P.V: saved batch summary (no CPV form) -->
    <div v-if="pcpvLoadedBatch" class="payment-purchase-section-pad pb-4 pt-2">
      <p class="text-[0.78rem] font-semibold text-[#1e293b] mb-2 m-0">
        Saved P.C.P.V · {{ pcpvLoadedBatch.cpvNumber }}
      </p>
      <div class="payment-purchase-card-scroll mb-4">
      <table class="payment-purchase-summary-table w-full text-sm border border-slate-200">
        <thead>
          <tr class="bg-slate-100">
            <th class="text-left px-3 py-1.5 font-semibold">Document</th>
            <th class="text-right px-3 py-1.5 font-semibold">Total amount</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="doc in pcpvLoadedBatch.documents"
            :key="doc.id"
            class="border-t border-slate-200"
          >
            <td class="px-3 py-1.5">{{ doc.label }}</td>
            <td class="px-3 py-1.5 text-right font-semibold">{{ fmt(doc.docPayableTotal) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="bg-slate-50 border-t-2 border-slate-300">
            <td class="px-3 py-2 font-bold">Batch total</td>
            <td class="px-3 py-2 text-right font-bold text-[#0d4169]">
              {{ fmt(pcpvLoadedBatch.batchSummary?.batchPayableTotal) }}
            </td>
          </tr>
        </tfoot>
      </table>
      </div>
      <div class="payment-purchase-batch-meta grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-[0.75rem] max-w-2xl">
        <div><span class="text-slate-500">Base:</span> {{ fmt(pcpvLoadedBatch.batchSummary?.baseAmount) }}</div>
        <div><span class="text-slate-500">Taxable:</span> {{ fmt(pcpvLoadedBatch.batchSummary?.taxableAmount) }}</div>
        <div><span class="text-slate-500">Tax:</span> {{ fmt(pcpvLoadedBatch.batchSummary?.taxAmount) }}</div>
        <div><span class="text-slate-500">Grand total:</span> {{ fmt(pcpvLoadedBatch.batchSummary?.grandTotal) }}</div>
        <div><span class="text-slate-500">Additional:</span> {{ fmt(pcpvLoadedBatch.batchSummary?.additionalAmount) }}</div>
      </div>
    </div>

    <div
      v-if="tableMode && !pcpvLoadedBatch"
      class="payment-purchase-section-pad payment-purchase-main pb-6 pt-2"
    >
      <p class="text-[0.78rem] font-semibold text-[#1e293b] mb-3 m-0">
        {{ tableTitle }}
        <span v-if="draft.paymentType" class="text-slate-500 font-normal">
          · {{ draft.paymentType }}
        </span>
      </p>

      <div v-if="showReceiptMetaFields()" class="receipt-meta-row mb-4">
        <div class="receipt-field">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Seller TIN</label>
          <input
            v-model.trim="draft.sellerTin"
            type="text"
            class="w-full"
            autocomplete="off"
            @input="onSellerTinInput"
          >
        </div>
        <div class="receipt-field">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Seller Name</label>
          <input
            v-model.trim="draft.sellerName"
            type="text"
            class="w-full"
            autocomplete="off"
          >
        </div>
        <div class="receipt-field">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Receipt Number (FS No)</label>
          <input
            v-model.trim="draft.receiptNumber"
            type="text"
            class="w-full"
            autocomplete="off"
          >
        </div>
        <div class="receipt-field">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Receipt date</label>
          <input
            v-model="draft.receiptDate"
            type="date"
            class="w-full cursor-pointer"
            required
          >
        </div>
        <!-- <div class="receipt-field flex flex-col gap-1 w-full lg:flex-1 lg:min-w-0">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Calendar Type</label>
          <select
            v-model="draft.calendarType"
            class="w-full cursor-pointer"
          >
            <option value="" disabled>Select calendar</option>
            <option
              v-for="opt in calendarTypeOptions"
              :key="opt"
              :value="opt"
            >
              {{ opt }}
            </option>
          </select>
        </div> -->
        <div class="receipt-field">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">MRC Number</label>
          <input
            :value="draft.mrc"
            type="text"
            inputmode="numeric"
            maxlength="10"
            class="w-full font-mono tracking-wide"
            autocomplete="off"
            placeholder=""
            @input="onMrcInput"
          >
        </div>
      </div>

      <div class="purchase-category-row mb-4">
        <div class="purchase-category-field">
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">Purchase Category</label>
          <select
            v-model="draft.purchaseCategory"
            class="w-full cursor-pointer"
          >
            <option value="" disabled>Select purchase category</option>
            <option
              v-for="opt in purchaseCategoryOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div
          v-if="isPcpvPaymentType() && pcpvAddedDocuments.length > 0"
          class="purchase-category-field"
        >
          <label class="text-[0.78rem] font-semibold text-[#1e293b] text-left">View Summary</label>
          <select
            v-model="pcpvSelectedAddedDocKey"
            class="w-full cursor-pointer"
          >
            <option value="" disabled>Payment Summary</option>
            <option
              v-for="entry in pcpvAddedDocuments"
              :key="entry.key"
              :value="entry.key"
            >
              {{ entry.label }} — {{ fmt(entry.docPayableTotal) }}
            </option>
          </select>
        </div>
      </div>

      <div class="payment-table-wrap" :class="{ 'payment-table-wrap--cpv': showChequePaymentVoucher }">
      <div class="payment-table-scroll custom-scrollbar w-full">
        <div class="payment-table-h-scroll__inner">
        <!-- GRV table (also unified P.C.P.V view for all document types) -->
        <table
          v-if="showGrvStyleTable"
          class="payroll-table payment-purchase-table border-collapse"
        >
          <colgroup>
            <col class="col-idx">
            <col class="col-code">
            <col class="col-doc">
            <col class="col-pr">
            <col class="col-supplier">
            <col class="col-category">
            <col class="col-item">
            <col class="col-purchase-cat">
            <col class="col-purchase-type">
            <col class="col-vol">
            <col class="col-qty">
            <col class="col-price">
            <col class="col-total">
            <col class="col-expiry">
            <col class="col-taxable">
            <col class="col-remark">
          </colgroup>
          <thead>
            <tr>
              <th class="col-idx">#</th>
              <th class="col-code">Code</th>
              <th class="col-doc">{{ showPcpvUnifiedTable ? 'Grv No/NS No' : 'Grv No' }}</th>
              <th class="col-pr">Pr No</th>
              <th class="col-supplier">Supplier</th>
              <th class="col-category">Category</th>
              <th class="col-item">Item</th>
              <th class="col-purchase-cat">Purchase Category</th>
              <th class="col-purchase-type">Purchase Type</th>
              <th class="num col-vol">Volume</th>
              <th class="num col-qty">Qty</th>
              <th class="num col-price">Unit Price</th>
              <th class="num col-total">Total Price</th>
              <th class="col-expiry">Expiry</th>
              <th class="col-taxable">Taxable</th>
              <th class="col-remark">Remark</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="grvTableRows.length === 0">
              <td colspan="16" class="py-10 text-center text-slate-400 italic text-sm">
                {{ showPcpvUnifiedTable ? 'No payment items generated yet.' : 'No GRV records found for the entered number.' }}
              </td>
            </tr>
            <tr
              v-for="row in grvTableRows"
              :key="row.key"
              class="hover:bg-slate-50 transition-colors border-b"
              :class="row.isManual ? 'manual-row' : ''"
            >
              <td class="col-idx">
                <div v-if="row.isManual" class="col-idx-actions">
                  <span class="col-idx-num">{{ row.no }}</span>
                  <button
                    type="button"
                    class="remove-row-btn"
                    title="Remove row"
                    aria-label="Remove row"
                    @click="removeGrvStyleTableManualRow(row)"
                  >
                    ×
                  </button>
                </div>
                <template v-else>{{ row.no }}</template>
              </td>
              <td class="col-code">{{ displayRowCode(row) }}</td>
              <td class="col-doc">{{ showPcpvUnifiedTable ? pcpvRowDocNo(row) : row.grvNo }}</td>
              <td class="col-pr">{{ row.prNo }}</td>
              <td class="col-supplier">{{ row.supplier }}</td>
              <td class="col-category">
                <div v-if="row.isManual" class="manual-item-stack">
                  <select
                    v-model="row.chartOfAccountId"
                    class="manual-cell-select manual-account-select"
                    @change="onManualGrvAccountChange(row)"
                  >
                    <option value="">Account</option>
                    <option
                      v-for="acc in enabledChartAccounts"
                      :key="'grv-acc-' + acc.id"
                      :value="String(acc.id)"
                    >
                      {{ acc.accountName }}
                    </option>
                  </select>
                </div>
                <template v-else>{{ row.category }}</template>
              </td>
              <td class="col-item" :class="!row.isManual && row.isProduct ? 'text-green-600 font-semibold' : ''">
                <input
                  v-if="row.isManual"
                  v-model="row.item"
                  type="text"
                  class="remark-table-field"
                  placeholder=""
                >
                <template v-else>{{ row.item }}</template>
              </td>
              <td class="col-purchase-cat">
                <div class="purchase-cat-cell">
                  <input
                    v-model="row.usePurchaseCategory"
                    type="checkbox"
                    class="purchase-cat-checkbox"
                    :disabled="!purchaseCategoryDropdownSelected"
                    @change="onRowPurchaseCategoryToggle(row)"
                  >
                  <span
                    v-if="row.usePurchaseCategory"
                    class="purchase-cat-label"
                    :title="purchaseCategoryLabel(row.purchaseCategory)"
                  >
                    {{ purchaseCategoryLabel(row.purchaseCategory) }}
                  </span>
                </div>
              </td>
              <td class="col-purchase-type">
                <select
                  v-if="isPurchaseTypeEditable(row)"
                  :value="row.purchaseType"
                  class="manual-cell-select purchase-type-select"
                  @change="onRowPurchaseTypeChange(row, $event.target.value)"
                >
                  <option
                    v-for="opt in purchaseTypeOptions"
                    :key="'pt-' + row.key + '-' + opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
                <span v-else class="purchase-type-locked">{{ PURCHASE_TYPE_SERVICE }}</span>
              </td>
              <td class="num col-vol">{{ row.isManual ? '' : row.volume }}</td>
              <td class="num col-qty">
                <input
                  v-if="row.isManual"
                  v-model="row.qty"
                  type="text"
                  inputmode="decimal"
                  class="manual-cell-input manual-cell-input-narrow"
                  @input="onManualGrvQtyPrice(row)"
                >
                <template v-else>{{ row.qty }}</template>
              </td>
              <td class="num col-price">
                <input
                  v-if="row.isManual"
                  v-model="row.unitPrice"
                  type="text"
                  inputmode="decimal"
                  class="manual-cell-input manual-cell-input-narrow"
                  @input="onManualGrvQtyPrice(row)"
                >
                <template v-else>{{ fmt(row.unitPrice) }}</template>
              </td>
              <td class="num col-total">{{ fmt(showPcpvUnifiedTable ? pcpvRowLineTotal(row) : row.totalPrice) }}</td>
              <td class="col-expiry">{{ row.expiry }}</td>
              <td class="col-taxable">
                <span
                  v-if="!row.isManual"
                  class="status-pill text-[0.6rem]"
                  :class="row.isTaxable ? 'active' : 'inactive'"
                >
                  {{ row.taxable }}
                </span>
                <input
                  v-else
                  v-model="row.isTaxable"
                  type="checkbox"
                  class="taxable-checkbox"
                  title="Taxable"
                  @change="onRowTaxableToggle(row)"
                >
              </td>
              <td class="col-remark">
                <input
                  v-model="row.remark"
                  type="text"
                  class="remark-table-field"
                  placeholder=""
                >
              </td>
            </tr>
            <tr class="add-row-tr border-b">
              <td class="col-idx">
                <button
                  type="button"
                  class="add-row-btn"
                  title="Add empty row"
                  @click="addGrvStyleTableEmptyRow"
                >
                  +
                </button>
              </td>
              <td colspan="15"></td>
            </tr>
          </tbody>
        </table>

        <!-- Non-stockable table -->
        <table
          v-else-if="tableMode === 'ns'"
          class="payroll-table payment-purchase-table border-collapse"
        >
          <colgroup>
            <col class="col-idx">
            <col class="col-code">
            <col class="col-doc">
            <col class="col-pr">
            <col class="col-supplier">
            <col class="col-category">
            <col class="col-dept">
            <col class="col-item">
            <col class="col-purchase-cat">
            <col class="col-purchase-type">
            <col class="col-vol">
            <col class="col-qty">
            <col class="col-price">
            <col class="col-total">
            <col class="col-taxable">
            <col class="col-remark">
          </colgroup>
          <thead>
            <tr>
              <th class="col-idx">#</th>
              <th class="col-code">Code</th>
              <th class="col-doc">NS No</th>
              <th class="col-pr">PR No</th>
              <th class="col-supplier">Supplier</th>
              <th class="col-category">Category</th>
              <th class="col-dept">Dept</th>
              <th class="col-item">Item</th>
              <th class="col-purchase-cat">Purchase Category</th>
              <th class="col-purchase-type">Purchase Type</th>
              <th class="num col-vol">Volume</th>
              <th class="num col-qty">Qty</th>
              <th class="num col-price">Unit Price</th>
              <th class="num col-total">Total</th>
              <th class="col-taxable">Taxable</th>
              <th class="col-remark">Remark</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="nsAllRows.length === 0">
              <td colspan="17" class="py-10 text-center text-slate-400 italic text-sm">
                No non-stockable records found for the entered number.
              </td>
            </tr>
            <tr
              v-for="row in nsAllRows"
              :key="row.key"
              class="hover:bg-slate-50 transition-colors border-b"
              :class="row.isManual ? 'manual-row' : ''"
            >
              <td class="col-idx">
                <div v-if="row.isManual" class="col-idx-actions">
                  <span class="col-idx-num">{{ row.no }}</span>
                  <button
                    type="button"
                    class="remove-row-btn"
                    title="Remove row"
                    aria-label="Remove row"
                    @click="removeNsManualRow(row)"
                  >
                    ×
                  </button>
                </div>
                <template v-else>{{ row.no }}</template>
              </td>
              <td class="col-code">{{ displayRowCode(row) }}</td>
              <td class="col-doc">{{ row.nsNo }}</td>
              <td class="col-pr">{{ row.prNo }}</td>
              <td class="col-supplier">{{ row.supplier }}</td>
              <td class="col-category">
                <div v-if="row.isManual" class="manual-item-stack">
                  <select
                    v-model="row.chartOfAccountId"
                    class="manual-cell-select manual-account-select"
                    @change="onManualNsAccountChange(row)"
                  >
                    <option value="">Account</option>
                    <option
                      v-for="acc in enabledChartAccounts"
                      :key="'ns-acc-' + acc.id"
                      :value="String(acc.id)"
                    >
                      {{ acc.accountName }}
                    </option>
                  </select>
                </div>
                <template v-else>{{ row.category }}</template>
              </td>
              <td class="col-dept">{{ row.dept }}</td>
              <td class="col-item" :class="!row.isManual && row.isProduct ? 'text-green-600 font-semibold' : ''">
                <input
                  v-if="row.isManual"
                  v-model="row.item"
                  type="text"
                  class="remark-table-field"
                  placeholder=""
                >
                <template v-else>{{ row.item }}</template>
              </td>
              <td class="col-purchase-cat">
                <div class="purchase-cat-cell">
                  <input
                    v-model="row.usePurchaseCategory"
                    type="checkbox"
                    class="purchase-cat-checkbox"
                    :disabled="!purchaseCategoryDropdownSelected"
                    @change="onRowPurchaseCategoryToggle(row)"
                  >
                  <span
                    v-if="row.usePurchaseCategory"
                    class="purchase-cat-label"
                    :title="purchaseCategoryLabel(row.purchaseCategory)"
                  >
                    {{ purchaseCategoryLabel(row.purchaseCategory) }}
                  </span>
                </div>
              </td>
              <td class="col-purchase-type">
                <select
                  v-if="isPurchaseTypeEditable(row)"
                  :value="row.purchaseType"
                  class="manual-cell-select purchase-type-select"
                  @change="onRowPurchaseTypeChange(row, $event.target.value)"
                >
                  <option
                    v-for="opt in purchaseTypeOptions"
                    :key="'pt-' + row.key + '-' + opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
                <span v-else class="purchase-type-locked">{{ PURCHASE_TYPE_SERVICE }}</span>
              </td>
              <td class="num col-vol">{{ row.isManual ? '' : row.volume }}</td>
              <td class="num col-qty">
                <input
                  v-if="row.isManual"
                  v-model="row.qty"
                  type="text"
                  inputmode="decimal"
                  class="manual-cell-input manual-cell-input-narrow"
                  @input="onManualNsQtyPrice(row)"
                >
                <template v-else>{{ row.qty }}</template>
              </td>
              <td class="num col-price">
                <input
                  v-if="row.isManual"
                  v-model="row.unitPrice"
                  type="text"
                  inputmode="decimal"
                  class="manual-cell-input manual-cell-input-narrow"
                  @input="onManualNsQtyPrice(row)"
                >
                <template v-else>{{ fmt(row.unitPrice) }}</template>
              </td>
              <td class="num col-total">{{ fmt(row.total) }}</td>
              <td class="col-taxable">
                <span
                  v-if="!row.isManual"
                  class="status-pill text-[0.6rem]"
                  :class="row.isTaxable ? 'active' : 'inactive'"
                >
                  {{ row.taxable }}
                </span>
                <input
                  v-else
                  v-model="row.isTaxable"
                  type="checkbox"
                  class="taxable-checkbox"
                  title="Taxable"
                  @change="onRowTaxableToggle(row)"
                >
              </td>
              <td class="col-remark">
                <input
                  v-model="row.remark"
                  type="text"
                  class="remark-table-field"
                  placeholder=""
                >
              </td>
            </tr>
            <tr class="add-row-tr border-b">
              <td class="col-idx">
                <button
                  type="button"
                  class="add-row-btn"
                  title="Add empty row"
                  @click="addNsEmptyRow"
                >
                  +
                </button>
              </td>
              <td colspan="16"></td>
            </tr>
          </tbody>
        </table>

        <!-- Other (same columns as GRV table; manual lines only) -->
        <table
          v-else-if="tableMode === 'other'"
          class="payroll-table payment-purchase-table border-collapse"
        >
          <colgroup>
            <col class="col-idx">
            <col class="col-code">
            <col class="col-doc">
            <col class="col-pr">
            <col class="col-supplier">
            <col class="col-category">
            <col class="col-item">
            <col class="col-purchase-cat">
            <col class="col-purchase-type">
            <col class="col-vol">
            <col class="col-qty">
            <col class="col-price">
            <col class="col-total">
            <col class="col-expiry">
            <col class="col-taxable">
            <col class="col-remark">
          </colgroup>
          <thead>
            <tr>
              <th class="col-idx">#</th>
              <th class="col-code">Code</th>
              <th class="col-doc">Grv No</th>
              <th class="col-pr">Pr No</th>
              <th class="col-supplier">Supplier</th>
              <th class="col-category">Category</th>
              <th class="col-item">Item</th>
              <th class="col-purchase-cat">Purchase Category</th>
              <th class="col-purchase-type">Purchase Type</th>
              <th class="num col-vol">Volume</th>
              <th class="num col-qty">Qty</th>
              <th class="num col-price">Unit Price</th>
              <th class="num col-total">Total Price</th>
              <th class="col-expiry">Expiry</th>
              <th class="col-taxable">Taxable</th>
              <th class="col-remark">Remark</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="otherAllRows.length === 0">
              <td colspan="16" class="py-10 text-center text-slate-400 italic text-sm">
                No lines yet. Use + to add a row.
              </td>
            </tr>
            <tr
              v-for="row in otherAllRows"
              :key="row.key"
              class="hover:bg-slate-50 transition-colors border-b manual-row"
            >
              <td class="col-idx">
                <div class="col-idx-actions">
                  <span class="col-idx-num">{{ row.no }}</span>
                  <button
                    type="button"
                    class="remove-row-btn"
                    title="Remove row"
                    aria-label="Remove row"
                    @click="removeOtherManualRow(row)"
                  >
                    ×
                  </button>
                </div>
              </td>
              <td class="col-code">{{ displayRowCode(row) }}</td>
              <td class="col-doc">{{ row.grvNo || '—' }}</td>
              <td class="col-pr">{{ row.prNo || '—' }}</td>
              <td class="col-supplier">{{ row.supplier || '—' }}</td>
              <td class="col-category">
                <div class="manual-item-stack">
                  <select
                    v-model="row.chartOfAccountId"
                    class="manual-cell-select manual-account-select"
                    @change="onManualOtherAccountChange(row)"
                  >
                    <option value="">Account</option>
                    <option
                      v-for="acc in enabledChartAccounts"
                      :key="'other-acc-' + acc.id"
                      :value="String(acc.id)"
                    >
                      {{ acc.accountName }}
                    </option>
                  </select>
                </div>
              </td>
              <td class="col-item">
                <input
                  v-model="row.item"
                  type="text"
                  class="remark-table-field"
                  placeholder=""
                >
              </td>
              <td class="col-purchase-cat">
                <div class="purchase-cat-cell">
                  <input
                    v-model="row.usePurchaseCategory"
                    type="checkbox"
                    class="purchase-cat-checkbox"
                    :disabled="!purchaseCategoryDropdownSelected"
                    @change="onRowPurchaseCategoryToggle(row)"
                  >
                  <span
                    v-if="row.usePurchaseCategory"
                    class="purchase-cat-label"
                    :title="purchaseCategoryLabel(row.purchaseCategory)"
                  >
                    {{ purchaseCategoryLabel(row.purchaseCategory) }}
                  </span>
                </div>
              </td>
              <td class="col-purchase-type">
                <select
                  v-if="isPurchaseTypeEditable(row)"
                  :value="row.purchaseType"
                  class="manual-cell-select purchase-type-select"
                  @change="onRowPurchaseTypeChange(row, $event.target.value)"
                >
                  <option
                    v-for="opt in purchaseTypeOptions"
                    :key="'pt-' + row.key + '-' + opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
                <span v-else class="purchase-type-locked">{{ PURCHASE_TYPE_SERVICE }}</span>
              </td>
              <td class="num col-vol"></td>
              <td class="num col-qty">
                <input
                  v-model="row.qty"
                  type="text"
                  inputmode="decimal"
                  class="manual-cell-input manual-cell-input-narrow"
                  @input="onManualOtherQtyPrice(row)"
                >
              </td>
              <td class="num col-price">
                <input
                  v-model="row.unitPrice"
                  type="text"
                  inputmode="decimal"
                  class="manual-cell-input manual-cell-input-narrow"
                  @input="onManualOtherQtyPrice(row)"
                >
              </td>
              <td class="num col-total">{{ fmt(row.totalPrice) }}</td>
              <td class="col-expiry">{{ row.expiry || '—' }}</td>
              <td class="col-taxable">
                <input
                  v-model="row.isTaxable"
                  type="checkbox"
                  class="taxable-checkbox"
                  title="Taxable"
                  @change="onRowTaxableToggle(row)"
                >
              </td>
              <td class="col-remark">
                <input
                  v-model="row.remark"
                  type="text"
                  class="remark-table-field"
                  placeholder=""
                >
              </td>
            </tr>
            <tr class="add-row-tr border-b">
              <td class="col-idx">
                <button
                  type="button"
                  class="add-row-btn"
                  title="Add empty row"
                  @click="addOtherEmptyRow"
                >
                  +
                </button>
              </td>
              <td colspan="15"></td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>

      <div
        v-if="tableMode && (showGrvStyleTable ? grvTableRows.length > 0 : tableMode === 'ns' ? nsAllRows.length > 0 : otherAllRows.length > 0)"
        class="payment-purchase-table-footer"
      >
        <div v-if="showChequePaymentVoucherInTable" class="footer-cpv-panel">
          <ChequePaymentVoucherForm
            ref="cpvFormRef"
            :company-name="resolvedCompanyName"
            :voucher-no="cpvVoucherNo"
            :paid-to="cpvPaidTo"
            :amount-in-figures="cpvAmountInFigures"
            :active-banks="cpvActiveBanks"
            :bank-error="cpvBankError"
            :prepared-by-name="cpvPreparedByName"
            @bank-selected="onCpvBankSelected"
            @bank-limit-exceeded="onCpvBankLimitExceeded"
          />
        </div>

        <div class="num totals-amount-block">
          <div class="totals-amount-grid">
            <template v-if="showGrvStyleTable">
              <div class="totals-amount-row">
                <span class="totals-label">Base Amount:</span>
                <span class="totals-value">{{ fmt(grvFooterSummary.baseAmount) }}</span>
              </div>
              <div class="totals-amount-row">
                <span class="totals-label">Taxable Amount:</span>
                <span class="totals-value">{{ fmt(grvFooterSummary.taxableAmount) }}</span>
              </div>
              <div class="totals-amount-row">
                <span class="totals-label">Tax ({{ grvFooterSummary.vatPct }}%):</span>
                <span class="totals-value">{{ fmt(grvFooterSummary.taxAmount) }}</span>
              </div>
              <div class="totals-amount-row totals-amount-row--grand">
                <span class="totals-label">Grand total:</span>
                <span class="totals-value">{{ fmt(grvFooterGrandTotalDisplay) }}</span>
              </div>
              <template v-if="showWithholdingTotals()&& !isCompanySol">
                <div class="totals-amount-row">
                  <span class="totals-label">Service:</span>
                  <span class="totals-value">{{ fmt(grvCategoryBreakdown.serviceBase) }}</span>
                </div>
                <div class="totals-amount-row">
                  <span class="totals-label">Goods:</span>
                  <span class="totals-value">{{ fmt(grvCategoryBreakdown.goodsBase) }}</span>
                </div>
                <div class="totals-amount-row totals-amount-row--grand">
                  <span class="totals-label">Withholding:</span>
                  <span class="totals-value">{{ fmt(combinedWithholdingTotal(grvWithholding)) }}</span>
                </div>
                <div v-if="showGrvFooterAdditionalAmount" class="totals-amount-row">
                  <span class="totals-label">Additional Amt:</span>
                  <span class="totals-value">{{ fmt(grvFooterAdditional) }}</span>
                </div>
                <div class="totals-amount-row">
                  <span class="totals-label">Taxable Additional Amt:</span>
                  <span class="totals-value">{{ fmt(grvAdditionalTaxBreakdown.taxableAdditionalAmt) }}</span>
                </div>
                <div class="totals-amount-row">
                  <span class="totals-label">Additional Tax ({{ grvAdditionalTaxBreakdown.additionalTaxPct }}%):</span>
                  <span class="totals-value">{{ fmt(grvAdditionalTaxBreakdown.additionalTax) }}</span>
                </div>
                <div class="totals-amount-row totals-amount-row--grand">
                  <span class="totals-label">Additional Grand Total:</span>
                  <span class="totals-value">{{ fmt(grvAdditionalTaxBreakdown.additionalGrandTotal) }}</span>
                </div>
                <div class="totals-amount-row">
                  <span class="totals-label">Additional Service:</span>
                  <span class="totals-value">{{ fmt(grvAdditionalCategoryBreakdown.serviceBase) }}</span>
                </div>
                <div class="totals-amount-row">
                  <span class="totals-label">Additional Goods:</span>
                  <span class="totals-value">{{ fmt(grvAdditionalCategoryBreakdown.goodsBase) }}</span>
                </div>
                <div class="totals-amount-row totals-amount-row--grand">
                  <span class="totals-label">Additional Withholding:</span>
                  <span class="totals-value">{{ fmt(combinedWithholdingTotal(grvAdditionalWithholding)) }}</span>
                </div>
              </template>
            </template>

            <template v-else-if="tableMode === 'ns'">
              <div class="totals-amount-row">
                <span class="totals-label">Base Amount:</span>
                <span class="totals-value">{{ fmt(nsFooterSummary.baseAmount) }}</span>
              </div>
              <div class="totals-amount-row">
                <span class="totals-label">Taxable Amount:</span>
                <span class="totals-value">{{ fmt(nsFooterSummary.taxableAmount) }}</span>
              </div>
              <div class="totals-amount-row">
                <span class="totals-label">Tax ({{ nsFooterSummary.vatPct }}%):</span>
                <span class="totals-value">{{ fmt(nsFooterSummary.taxAmount) }}</span>
              </div>
              <div class="totals-amount-row totals-amount-row--grand">
                <span class="totals-label">Grand total:</span>
                <span class="totals-value">{{ fmt(nsFooterSummary.grandTotal) }}</span>
              </div>
              <template v-if="showWithholdingTotals()&& !isCompanySol">
                <div class="totals-amount-row">
                  <span class="totals-label">Service:</span>
                  <span class="totals-value">{{ fmt(nsCategoryBreakdown.serviceBase) }}</span>
                </div>
                <div class="totals-amount-row">
                  <span class="totals-label">Goods:</span>
                  <span class="totals-value">{{ fmt(nsCategoryBreakdown.goodsBase) }}</span>
                </div>
                <div class="totals-amount-row totals-amount-row--grand">
                  <span class="totals-label">Withholding:</span>
                  <span class="totals-value">{{ fmt(combinedWithholdingTotal(nsWithholding)) }}</span>
                </div>
                <div class="totals-amount-row">
                  <span class="totals-label">Additional Amt:</span>
                  <span class="totals-value">{{ fmt(nsFooterAdditional) }}</span>
                </div>
                <div class="totals-amount-row">
                  <span class="totals-label">Taxable Additional Amt:</span>
                  <span class="totals-value">{{ fmt(nsAdditionalTaxBreakdown.taxableAdditionalAmt) }}</span>
                </div>
                <div class="totals-amount-row">
                  <span class="totals-label">Additional Tax ({{ nsAdditionalTaxBreakdown.additionalTaxPct }}%):</span>
                  <span class="totals-value">{{ fmt(nsAdditionalTaxBreakdown.additionalTax) }}</span>
                </div>
                <div class="totals-amount-row totals-amount-row--grand">
                  <span class="totals-label">Additional Grand Total:</span>
                  <span class="totals-value">{{ fmt(nsAdditionalTaxBreakdown.additionalGrandTotal) }}</span>
                </div>
                <div class="totals-amount-row">
                  <span class="totals-label">Additional Service:</span>
                  <span class="totals-value">{{ fmt(nsAdditionalCategoryBreakdown.serviceBase) }}</span>
                </div>
                <div class="totals-amount-row">
                  <span class="totals-label">Additional Goods:</span>
                  <span class="totals-value">{{ fmt(nsAdditionalCategoryBreakdown.goodsBase) }}</span>
                </div>
                <div class="totals-amount-row totals-amount-row--grand">
                  <span class="totals-label">Additional Withholding:</span>
                  <span class="totals-value">{{ fmt(combinedWithholdingTotal(nsAdditionalWithholding)) }}</span>
                </div>
              </template>
            </template>

            <template v-else>
              <div class="totals-amount-row">
                <span class="totals-label">Base Amount:</span>
                <span class="totals-value">{{ fmt(otherFooterSummary.baseAmount) }}</span>
              </div>
              <div class="totals-amount-row">
                <span class="totals-label">Taxable Amount:</span>
                <span class="totals-value">{{ fmt(otherFooterSummary.taxableAmount) }}</span>
              </div>
              <div class="totals-amount-row">
                <span class="totals-label">Tax ({{ otherFooterSummary.vatPct }}%):</span>
                <span class="totals-value">{{ fmt(otherFooterSummary.taxAmount) }}</span>
              </div>
              <div class="totals-amount-row totals-amount-row--grand">
                <span class="totals-label">Grand total:</span>
                <span class="totals-value">{{ fmt(otherFooterGrandTotalDisplay) }}</span>
              </div>
              <div v-if="showOtherFooterAdditionalAmount" class="totals-amount-row">
                <span class="totals-label">Additional Amt:</span>
                <span class="totals-value">{{ fmt(otherFooterAdditionalDisplay) }}</span>
              </div>
              <template v-if="showWithholdingTotals() && !isCompanySol">
                <div class="totals-amount-row">
                  <span class="totals-label">Service:</span>
                  <span class="totals-value">{{ fmt(otherCategoryBreakdown.serviceBase) }}</span>
                </div>
                <div class="totals-amount-row">
                  <span class="totals-label">Goods:</span>
                  <span class="totals-value">{{ fmt(otherCategoryBreakdown.goodsBase) }}</span>
                </div>
                <div class="totals-amount-row totals-amount-row--grand">
                  <span class="totals-label">Withholding:</span>
                  <span class="totals-value">{{ fmt(combinedWithholdingTotal(otherWithholding)) }}</span>
                </div>
              </template>
            </template>
          </div>
        </div>
      </div>

      <div
        v-if="tableMode && !pcpvLoadedBatch"
        class="payment-purchase-actions flex justify-center items-center gap-3 pt-4 pb-2"
      >
        <button
          v-if="isSingleVoucherPaymentType()"
          type="button"
          :disabled="!canSavePaymentPurchase || isSaving || !tableMode"
          class="payment-purchase-primary-btn min-w-[140px] px-6 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          @click="savePayment"
        >
          <div
            v-if="isSaving"
            class="h-4 w-4 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
          ></div>
          <span v-if="!isSaving">Save</span>
        </button>
        <button
          v-else-if="isPcpvPaymentType()"
          type="button"
          :disabled="!canSavePaymentPurchase || isSaving || !canSavePcpvBatch"
          class="payment-purchase-primary-btn min-w-[140px] px-6 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          @click="savePcpvBatch"
        >
          <div
            v-if="isSaving"
            class="h-4 w-4 border-2 border-white/30 border-t-transparent rounded-full animate-spin"
          ></div>
          <span v-if="!isSaving">Save</span>
        </button>
      </div>

      </div>
    </div>

    <FinanceToast :show="toast.show" :variant="toast.variant">{{ toast.message }}</FinanceToast>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { apiFetch } from '../../../utils/api'
import FinanceToast from '../FinanceToast.vue'
import ChequePaymentVoucherForm from './ChequePaymentVoucherForm.vue'

const store = useStore()
const companyStatus = ref('')
const companyConstants = ref({ tax: 15, addition: 10 })

const companyTaxPct = computed(() => Math.max(0, n(companyConstants.value.tax)))

const isCompanySol = computed(() => companyStatus.value === 'SOL')

async function loadCompanyConstants() {
  try {
    const res = await apiFetch('/settings/company-constants')
    if (!res.ok) return
    const data = await res.json().catch(() => null)
    if (!data || typeof data !== 'object') return
    companyConstants.value = {
      tax: n(data.tax) || 15,
      addition: n(data.addition) || 10
    }
  } catch {
    // Keep defaults when company constants cannot be loaded.
  }
}

async function loadCompanyProfile() {
  try {
    const response = await apiFetch('/finance/company-profile')
    if (!response.ok) return
    const parsed = await response.json().catch(() => null)
    if (!parsed || typeof parsed !== 'object') return
    companyStatus.value = String(parsed.companyStatus || '').trim()
  } catch {
    // Leave companyStatus empty; withholding issue stays available if profile cannot be loaded.
  }
}

onMounted(async () => {
  await loadCompanyProfile()
  await loadCompanyConstants()
  await loadWithholdingSettings()
  await loadAvailableDocNumbers()
})

const paymentTypeOptions = ['C.P.V', 'P.C.P.V', 'Direct Payment']
const PAYMENT_TYPE_CPV = 'C.P.V'
const PAYMENT_TYPE_PCPV = 'P.C.P.V'
const PAYMENT_TYPE_DIRECT = 'Direct Payment'
const ZERO_WITHHOLDING = { service: 0, goods: 0, total: 0 }

const purchaseCategoryOptions = [
  { value: 'local_purchase_of_capital_assets', label: 'Local Purchase of Capital Assets' },
  { value: 'imported_purchase_of_capital_assets', label: 'Imported Purchase of Capital Assets' },
  { value: 'local_purchase_of_inputs', label: 'Local Purchase of Inputs' },
  { value: 'imported_purchase_of_inputs', label: 'Imported Purchase of Inputs' },
  { value: 'general_expense_inputs_purchase', label: 'General Expense Inputs Purchase' },
  { value: 'tax_exempted_purchase_with_no_vat_or_uncollectible_inputs', label: 'Tax Exempted-purchase with no vat or uncollectible inputs' },

]


const PURCHASE_TYPE_GOODS = 'Goods'
const PURCHASE_TYPE_SERVICE = 'Service'
const purchaseTypeOptions = [
  { value: PURCHASE_TYPE_GOODS, label: 'Goods' },
  { value: PURCHASE_TYPE_SERVICE, label: 'Service' }
]
const WITHHOLDING_STORAGE_KEY = 'Mina_finance_advanced_withholding'
// const calendarTypeOptions = ['Ethiopian', 'Gregorian']

const resolvedCompanyName = computed(() => {
  const user = store.getters.currentUser || store.state.user || {}
  return (
    user?.company?.name ||
    user?.companyName ||
    user?.company_name ||
    user?.company?.company_name ||
    'Company Name'
  )
})

const currentUser = computed(() => store.getters.currentUser || store.state.user || {})

const userPrivileges = computed(() => {
  const raw = currentUser.value?.privilege
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }
  return raw
})

const hasAllPrivilege = computed(() => {
  return userPrivileges.value?.All === true || userPrivileges.value?.all === true
})

function getPrivilegeEntry(key) {
  const priv = userPrivileges.value || {}
  if (!key) return null
  if (priv?.[key]) return priv[key]

  const normalizedNeedle = String(key).trim().toLowerCase()
  const foundKey = Object.keys(priv).find((k) => String(k).trim().toLowerCase() === normalizedNeedle)
  return foundKey ? priv[foundKey] : null
}

function getActionFlag(entry, action) {
  if (!entry || typeof entry !== 'object') return false
  if (!action) return false
  if (entry[action] !== undefined) return !!entry[action]

  const a = String(action)
  const candidates = [
    a.toLowerCase(),
    a.toUpperCase(),
    a.charAt(0).toUpperCase() + a.slice(1).toLowerCase()
  ]
  for (const k of candidates) {
    if (entry[k] !== undefined) return !!entry[k]
  }
  return false
}

const canAddPaymentPurchase = computed(() => {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry('Payment/ Purchase'), 'Add')
})

const canEditPaymentPurchase = computed(() => {
  if (hasAllPrivilege.value) return true
  return getActionFlag(getPrivilegeEntry('Payment/ Purchase'), 'Edit')
})

const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)

const loggedUserId = computed(() => toIntId(currentUser.value?.id || currentUser.value?.sub))

const loggedUserFullName = computed(() => {
  const u = currentUser.value
  return String(u?.full_name || u?.fullName || u?.username || '').trim()
})

/** Saved preparer user id; null = new record uses logged-in user. */
const cpvPreparedByUserId = ref(null)
const cpvPreparedByName = ref('')

function initCpvPreparedByForNew() {
  cpvPreparedByUserId.value = loggedUserId.value
  cpvPreparedByName.value = loggedUserFullName.value
}

function setCpvPreparedByFromSaved(saved) {
  const uid = toIntId(saved?.preparedByUserId)
  cpvPreparedByUserId.value = uid
  cpvPreparedByName.value = String(saved?.preparedBy || '').trim()
}

function clearCpvPreparedBy() {
  cpvPreparedByUserId.value = null
  cpvPreparedByName.value = ''
}

function cpvSignatureSaveFields() {
  const cpv = cpvFormRef.value?.getFormState?.() || {}
  const isNew = savedPaymentId.value == null && cpvPreparedByUserId.value == null
  const preparedByUserId = isNew
    ? loggedUserId.value
    : cpvPreparedByUserId.value || loggedUserId.value
  return {
    preparedByUserId,
    preparedBy: cpvPreparedByName.value || loggedUserFullName.value,
    preparedDate: cpv.preparedDate || '',
    checkedBy: '',
    checkedDate: '',
    approvedBy: '',
    approvedDate: '',
    receivedBy: '',
    receivedDate: ''
  }
}

const draft = reactive({
  grvNumber: '',
  nonStockableNumber: '',
  paymentType: '',
  sellerTin: '',
  sellerName: '',
  receiptNumber: '',
  receiptDate: '',
  calendarType: '',
  mrc: '',
  purchaseCategory: ''
})


function nextManualKey(kind) {
  manualRowSeq += 1
  return `manual-${kind}-${manualRowSeq}-${Date.now()}`
}

function normalizePurchaseType(value, row, mode) {
  const s = String(value || '').trim()
  if (s === PURCHASE_TYPE_SERVICE || s === PURCHASE_TYPE_GOODS) return s
  if (row?.isManual || mode === 'other') return PURCHASE_TYPE_SERVICE
  return PURCHASE_TYPE_GOODS
}

function createEmptyGrvRow() {
  const row = {
    key: nextManualKey('grv'),
    isManual: true,
    itemId: null,
    itemCode: '',
    chartOfAccountId: '',
    accountCode: '',
    accountName: '',
    accountType: '',
    grvNo: '',
    prNo: '',
    supplier: '',
    category: '',
    item: '',
    isProduct: false,
    qty: '',
    volume: '',
    unitPrice: '',
    totalPrice: 0,
    expiry: '',
    taxable: '',
    isTaxable: false,
    vat: 0,
    remark: '',
    usePurchaseCategory: false,
    purchaseCategory: '',
    purchaseType: PURCHASE_TYPE_SERVICE
  }
  return row
}

function createEmptyNsRow() {
  return {
    key: nextManualKey('ns'),
    isManual: true,
    itemId: null,
    itemCode: '',
    chartOfAccountId: '',
    accountCode: '',
    accountName: '',
    accountType: '',
    nsNo: '',
    prNo: '',
    supplier: '',
    category: '',
    dept: '',
    item: '',
    isProduct: false,
    qty: '',
    volume: '',
    unitPrice: '',
    total: 0,
    taxable: '',
    isTaxable: false,
    remark: '',
    vat: 0,
    usePurchaseCategory: false,
    purchaseCategory: '',
    purchaseType: PURCHASE_TYPE_SERVICE
  }
}

/** GRV-style table: C.P.V / Direct Payment use GRV extras; P.C.P.V unified view follows active tableMode. */
function addGrvStyleTableEmptyRow() {
  if (showPcpvUnifiedTable.value) {
    if (tableMode.value === 'ns') {
      addNsEmptyRow()
      return
    }
    if (tableMode.value === 'other') {
      addOtherEmptyRow()
      return
    }
  }
  grvExtraRows.value.push(createEmptyGrvRow())
}

function addNsEmptyRow() {
  nsExtraRows.value.push(createEmptyNsRow())
}

function createEmptyOtherRow() {
  return {
    key: nextManualKey('other'),
    isManual: true,
    itemId: null,
    itemCode: '',
    chartOfAccountId: '',
    accountCode: '',
    accountName: '',
    accountType: '',
    grvNo: '—',
    prNo: '—',
    supplier: '—',
    category: '',
    item: '',
    isProduct: false,
    qty: '',
    volume: '',
    unitPrice: '',
    totalPrice: 0,
    expiry: '',
    taxable: '',
    isTaxable: false,
    vat: 0,
    remark: '',
    usePurchaseCategory: false,
    purchaseCategory: '',
    purchaseType: PURCHASE_TYPE_SERVICE
  }
}

function addOtherEmptyRow() {
  otherExtraRows.value.push(createEmptyOtherRow())
}

const REMOVE_MANUAL_ROW_CONFIRM = 'Are you sure you want to remove this row?'

function removeRowByKey(rowsRef, key) {
  const i = rowsRef.value.findIndex((r) => r.key === key)
  if (i >= 0) rowsRef.value.splice(i, 1)
}

function requestRemoveManualRow(row, rowsRef) {
  if (!row?.key || !row.isManual) return
  if (!window.confirm(REMOVE_MANUAL_ROW_CONFIRM)) return
  removeRowByKey(rowsRef, row.key)
}

function removeRowByKeyIfPresent(rowsRef, key) {
  const i = rowsRef.value.findIndex((r) => r.key === key)
  if (i < 0) return false
  rowsRef.value.splice(i, 1)
  return true
}

function removePcpvQueuedManualRow(row) {
  const key = row.key
  for (let i = 0; i < pcpvQueue.value.length; i++) {
    const doc = pcpvQueue.value[i]
    const extraRows = doc.extraRows || []
    if (!extraRows.some((r) => r.key === key)) continue
    pcpvQueue.value[i] = buildPcpvDocQueueEntry({
      ...doc,
      mainRows: doc.mainRows || [],
      extraRows: extraRows.filter((r) => r.key !== key)
    })
    return
  }
}

/** GRV-style table: route remove to the array that holds the manual row (PCP.V unified mixes sources). */
function removeGrvStyleTableManualRow(row) {
  if (!row?.key || !row.isManual) return
  if (!window.confirm(REMOVE_MANUAL_ROW_CONFIRM)) return

  if (removeRowByKeyIfPresent(grvExtraRows, row.key)) return
  if (removeRowByKeyIfPresent(nsExtraRows, row.key)) return
  if (removeRowByKeyIfPresent(otherExtraRows, row.key)) return
  if (removeRowByKeyIfPresent(otherRows, row.key)) return

  if (showPcpvUnifiedTable.value) {
    removePcpvQueuedManualRow(row)
  }
}

function removeNsManualRow(row) {
  requestRemoveManualRow(row, nsExtraRows)
}

function removeOtherManualRow(row) {
  if (!row?.key) return
  if (!window.confirm(REMOVE_MANUAL_ROW_CONFIRM)) return
  if (otherExtraRows.value.some((r) => r.key === row.key)) {
    removeRowByKey(otherExtraRows, row.key)
  } else if (otherRows.value.some((r) => r.key === row.key)) {
    removeRowByKey(otherRows, row.key)
  }
}

function toggleOtherMode() {
  otherModeActive.value = !otherModeActive.value
  if (otherModeActive.value) {
    if (!isPcpvPaymentType()) {
      draft.grvNumber = ''
      draft.nonStockableNumber = ''
    }
  } else if (tableMode.value === 'other' && !isPcpvPaymentType()) {
    tableMode.value = null
    otherRows.value = []
    otherExtraRows.value = []
  }
}

function clearOtherModeSelection() {
  if (isPcpvPaymentType()) return
  if (!otherModeActive.value) return
  otherModeActive.value = false
  if (tableMode.value === 'other') {
    tableMode.value = null
    otherRows.value = []
    otherExtraRows.value = []
  }
}

function onGrvNumberInput() {
  if (!hasDocInput(draft.grvNumber)) return
  if (!isPcpvPaymentType()) {
    draft.nonStockableNumber = ''
    clearOtherModeSelection()
  }
}

function onNsNumberInput() {
  if (!hasDocInput(draft.nonStockableNumber)) return
  if (!isPcpvPaymentType()) {
    draft.grvNumber = ''
    clearOtherModeSelection()
  }
}

async function onManualOtherAccountChange(row) {
  await onManualGrvAccountChange(row)
}

function onManualOtherQtyPrice(row) {
  syncManualGrvTotals(row)
  bumpWithholdingRecalc()
}

function onMrcInput(e) {
  draft.mrc = String(e?.target?.value ?? '')
    .replace(/\D/g, '')
    .slice(0, 10)
}

function hasDocInput(v) {
  return String(v ?? '').trim() !== ''
}

/** C.P.V: one document filter at a time — clear others instead of disabling inputs. */
const otherModeActive = ref(false)

const isLoading = ref(false)
const isSaving = ref(false)
const savedPaymentId = ref(null)
const availableGrvNumbers = ref([])
const availableNsNumbers = ref([])
const loadingAvailableGrvNumbers = ref(false)
const loadingAvailableNsNumbers = ref(false)

const grvNumberOptions = computed(() => {
  const current = String(draft.grvNumber || '').trim()
  const options = [...availableGrvNumbers.value]
  if (current && !options.includes(current)) options.unshift(current)
  return options
})

const nsNumberOptions = computed(() => {
  const current = String(draft.nonStockableNumber || '').trim()
  const options = [...availableNsNumbers.value]
  if (current && !options.includes(current)) options.unshift(current)
  return options
})

const canSavePaymentPurchase = computed(() => {
  if (savedPaymentId.value != null) return canEditPaymentPurchase.value
  return canAddPaymentPurchase.value
})

const cpvFormRef = ref(null)
const cpvBankError = ref(false)
const pcpvQueue = ref([])
const pcpvLoadedBatch = ref(null)
const pcpvSelectedAddedDocKey = ref('')
const pcpvActiveOtherDocKey = ref('')
const tableMode = ref(null) // 'grv' | 'ns' | 'other' | null
const grvRows = ref([])
const nsRows = ref([])
const otherRows = ref([])
const grvExtraRows = ref([])
const nsExtraRows = ref([])
const otherExtraRows = ref([])
let manualRowSeq = 0
const chartAccounts = ref([])
const isLoadingChartAccounts = ref(false)

const toast = reactive({ show: false, message: '', variant: 'success' })

function pcpvRowDocNo(row) {
  const grv = String(row?.grvNo || '').trim()
  if (grv && grv !== '-') return grv
  const ns = String(row?.nsNo || '').trim()
  if (ns && ns !== '-') return ns
  return 'Other'
}

function pcpvRowLineTotal(row) {
  const amount = row?.totalPrice ?? row?.total
  const n = Number(amount)
  return Number.isFinite(n) ? n : 0
}

const pcpvAllDisplayRows = computed(() => {
  if (!isPcpvPaymentType()) return []
  const rows = []
  for (const doc of pcpvQueue.value) {
    rows.push(...(doc.mainRows || []), ...(doc.extraRows || []))
  }
  rows.push(
    ...grvRows.value,
    ...grvExtraRows.value,
    ...nsRows.value,
    ...nsExtraRows.value,
    ...otherRows.value,
    ...otherExtraRows.value
  )
  rows.forEach((r, i) => {
    r.no = i + 1
  })
  return rows
})

const showPcpvUnifiedTable = computed(
  () => isPcpvPaymentType() && !pcpvLoadedBatch.value && pcpvAllDisplayRows.value.length > 0
)

const tableTitle = computed(() => {
  if (showPcpvUnifiedTable.value) return 'Payment Items'
  if (tableMode.value === 'grv') return 'GRV Items'
  if (tableMode.value === 'ns') return 'Non-Stockable Items'
  if (tableMode.value === 'other') return 'Other Items'
  return ''
})

function footerVatPct(rows) {
  if (!rows?.length) return 0
  const first = rows[0]
  const v = Number(first?.vat)
  if (Number.isFinite(v) && v > 0) return v
  if (first?.isTaxable) return 15
  const taxableRow = rows.find((r) => r.isTaxable)
  if (taxableRow) {
    const tv = Number(taxableRow.vat)
    if (Number.isFinite(tv) && tv > 0) return tv
    return 15
  }
  return 0
}

function summarizeRows(rows, amountField) {
  const lineAmount = (r) => Number(r[amountField] || 0)
  const baseAmount = rows.reduce((sum, r) => sum + lineAmount(r), 0)
  const taxableAmount = rows.reduce(
    (sum, r) => (r.isTaxable ? sum + lineAmount(r) : sum),
    0
  )
  const vatPct = footerVatPct(rows)
  const taxAmount = taxableAmount * (vatPct / 100)
  const grandTotal = baseAmount + taxAmount
  return { baseAmount, taxableAmount, vatPct, taxAmount, grandTotal }
}

const grvAllRows = computed(() => {
  const all = [...grvRows.value, ...grvExtraRows.value]
  all.forEach((r, i) => {
    r.no = i + 1
  })
  return all
})

const nsAllRows = computed(() => {
  const all = [...nsRows.value, ...nsExtraRows.value]
  all.forEach((r, i) => {
    r.no = i + 1
  })
  return all
})

const otherAllRows = computed(() => {
  const all = [...otherRows.value, ...otherExtraRows.value]
  all.forEach((r, i) => {
    r.no = i + 1
  })
  return all
})

const showGrvStyleTable = computed(
  () => showPcpvUnifiedTable.value || tableMode.value === 'grv'
)

const grvTableRows = computed(() =>
  showPcpvUnifiedTable.value ? pcpvAllDisplayRows.value : grvAllRows.value
)

/** Full doc no from loaded lines (e.g. GRV-0015), not toolbar suffix-only input. */
function resolveFullGrvNumber() {
  for (const row of grvAllRows.value) {
    const no = String(row.grvNo || '').trim()
    if (no && no !== '-') return no
  }
  const input = String(draft.grvNumber || '').trim()
  return input || null
}

function resolveFullNsNumber() {
  for (const row of nsAllRows.value) {
    const no = String(row.nsNo || '').trim()
    if (no && no !== '-') return no
  }
  const input = String(draft.nonStockableNumber || '').trim()
  return input || null
}

const showChequePaymentVoucher = computed(() => {
  if (isPcpvPaymentType()) return pcpvAllDisplayRows.value.length > 0
  if (tableMode.value === 'grv') return grvAllRows.value.length > 0
  if (tableMode.value === 'ns') return nsAllRows.value.length > 0
  if (tableMode.value === 'other') return otherAllRows.value.length > 0
  return false
})

const showChequePaymentVoucherInTable = computed(() => showChequePaymentVoucher.value)

const isPcpvSessionLocked = computed(() => {
  if (!isPcpvPaymentType() || pcpvLoadedBatch.value) return false
  return pcpvQueue.value.length > 0 || !!tableMode.value
})

const pcpvAddedDocuments = computed(() => {
  const fromTable = listPcpvDocumentsFromTable()
  const tableKeys = new Set(fromTable.map((d) => d.key))
  const fromQueue = pcpvQueue.value.filter((d) => !tableKeys.has(d.key))
  return [...fromQueue, ...fromTable]
})

const pcpvBatchPayableTotal = computed(() =>
  pcpvQueue.value.reduce((sum, doc) => sum + n(doc.docPayableTotal), 0)
)

const pcpvPendingPayableTotal = computed(() => {
  if (!isPcpvPaymentType() || !tableMode.value) return 0
  return docPayableTotal(tableMode.value)
})

const pcpvLiveBatchPayableTotal = computed(() => {
  if (pcpvAddedDocuments.value.length > 0) {
    return pcpvAddedDocuments.value.reduce((sum, doc) => sum + n(doc.docPayableTotal), 0)
  }
  return pcpvBatchPayableTotal.value + pcpvPendingPayableTotal.value
})

function aggregatePcpvPayloadTotals(payloads) {
  let baseAmount = 0
  let taxableAmount = 0
  let taxAmount = 0
  let grandTotal = 0
  let additionalAmount = 0
  let taxableAdditionalAmount = 0
  let additionalTax = 0
  let additionalGrandTotal = 0
  let additionalServiceAmount = 0
  let additionalGoodsAmount = 0
  let additionalWithholdingAmount = 0
  let maxVatPct = 0
  for (const p of payloads) {
    baseAmount += n(p.baseAmount)
    taxableAmount += n(p.taxableAmount)
    taxAmount += n(p.taxAmount)
    grandTotal += n(p.grandTotal)
    additionalAmount += n(p.additionalAmount)
    taxableAdditionalAmount += n(p.taxableAdditionalAmount)
    additionalTax += n(p.additionalTax)
    additionalGrandTotal += n(p.additionalGrandTotal)
    additionalServiceAmount += n(p.additionalServiceAmount)
    additionalGoodsAmount += n(p.additionalGoodsAmount)
    additionalWithholdingAmount += n(p.additionalWithholdingAmount)
    if (n(p.taxPct) > maxVatPct) maxVatPct = n(p.taxPct)
  }
  return {
    baseAmount,
    taxableAmount,
    taxAmount,
    grandTotal,
    additionalAmount,
    taxableAdditionalAmount,
    additionalTax,
    additionalGrandTotal,
    additionalServiceAmount,
    additionalGoodsAmount,
    additionalWithholdingAmount,
    vatPct: maxVatPct
  }
}

function currentTableSavePayload() {
  if (!tableMode.value) return null
  const mode = tableMode.value
  const allRows =
    mode === 'grv' ? grvAllRows.value : mode === 'other' ? otherAllRows.value : nsAllRows.value
  if (!allRows.length) return null
  return buildDocumentSavePayload(mode)
}

const pcpvCombinedSummary = computed(() => {
  if (pcpvAddedDocuments.value.length > 0) {
    return aggregatePcpvPayloadTotals(pcpvAddedDocuments.value.map((entry) => entry.payload))
  }
  const payloads = pcpvQueue.value.map((entry) => entry.payload)
  const pending = currentTableSavePayload()
  if (pending) payloads.push(pending)
  return aggregatePcpvPayloadTotals(payloads)
})

const grvFooterSummary = computed(() =>
  isPcpvPaymentType() ? pcpvCombinedSummary.value : grvSummary.value
)
const nsFooterSummary = computed(() =>
  isPcpvPaymentType() ? pcpvCombinedSummary.value : nsSummary.value
)
const otherFooterSummary = computed(() =>
  isPcpvPaymentType() ? pcpvCombinedSummary.value : otherSummary.value
)

const grvFooterAdditional = computed(() =>
  isPcpvPaymentType() ? pcpvCombinedSummary.value.additionalAmount : grvAdditionalAmount.value
)
const nsFooterAdditional = computed(() =>
  isPcpvPaymentType() ? pcpvCombinedSummary.value.additionalAmount : nsAdditionalAmount.value
)

const otherFooterGrandTotalDisplay = computed(() => {
  if (isOtherTotalsDisplay()) {
    return otherGrandTotalFromSummary(otherSummary.value)
  }
  if (isPcpvPaymentType()) {
    return pcpvCombinedSummary.value.grandTotal
  }
  return otherFooterGrandTotal.value
})
const showOtherFooterAdditionalAmount = computed(() => !isOtherTotalsDisplay())

const pcpvSessionIsOtherOnly = computed(() => {
  if (!isPcpvPaymentType() || tableMode.value !== 'other') return false
  return !pcpvQueue.value.some((d) => d.mode !== 'other')
})

const grvFooterGrandTotalDisplay = computed(() => {
  if (pcpvSessionIsOtherOnly.value) {
    return otherGrandTotalFromSummary(summarizeRows(pcpvAllDisplayRows.value, 'totalPrice'))
  }
  return grvFooterSummary.value.grandTotal
})

const showGrvFooterAdditionalAmount = computed(() => !pcpvSessionIsOtherOnly.value)
const otherFooterAdditionalDisplay = computed(() =>
  isPcpvPaymentType() ? pcpvCombinedSummary.value.additionalAmount : otherFooterAdditionalAmount.value
)

const canSavePcpvBatch = computed(() => {
  if (!isPcpvPaymentType()) return false
  if (pcpvAddedDocuments.value.length > 0) return true
  if (pcpvQueue.value.length > 0) return true
  return !!currentTableSavePayload()
})

const cpvVoucherNo = ref('')
const cpvActiveBanks = ref([])

function bankRowIsActive(row) {
  if (!row || typeof row !== 'object') return false
  const a = row.active
  return a === true || a === 1 || a === '1'
}

async function loadCpvActiveBanks() {
  try {
    const res = await apiFetch('/finance/bank-register')
    if (!res.ok) {
      cpvActiveBanks.value = []
      return
    }
    const data = await res.json().catch(() => [])
    const list = Array.isArray(data) ? data : []
    cpvActiveBanks.value = list.filter(bankRowIsActive).map((row) => ({
      id: row.id,
      bankName:
        row.bankName != null && String(row.bankName).trim() !== ''
          ? String(row.bankName).trim()
          : '—'
    }))
  } catch {
    cpvActiveBanks.value = []
  }
}

async function fetchNextCpvNumber() {
  try {
    const res = await apiFetch('/finance/payment-purchase/next-cpv')
    if (!res.ok) return 'CPV-0001'
    const data = await res.json().catch(() => ({}))
    return String(data?.cpvNumber || 'CPV-0001')
  } catch {
    return 'CPV-0001'
  }
}

async function fetchSavedPayment(grvNumber, nsNumber) {
  const params = new URLSearchParams()
  if (grvNumber) params.set('grvNumber', grvNumber)
  if (nsNumber) params.set('nsNumber', nsNumber)
  try {
    const res = await apiFetch(`/finance/payment-purchase?${params}`)
    if (res.status === 403) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Not allowed to view saved payment purchase.', 'error')
      return null
    }
    if (!res.ok) return null
    const data = await res.json().catch(() => null)
    if (data == null || typeof data !== 'object') return null
    if (data.isPcpvBatch) return data
    return data.id != null ? data : null
  } catch {
    return null
  }
}

/** Load saved C.P.V / Direct Payment document on Generate when a matching payment_purchase row exists. */
function shouldLoadSavedCpvDocument(saved) {
  if (!saved || saved.isPcpvBatch || saved.id == null) return false
  if (!isSingleVoucherPaymentType()) return false
  const savedType = String(saved.paymentType || '').trim()
  const currentType = String(draft.paymentType || '').trim()
  if (savedType && savedType !== currentType) return false
  return true
}

async function fetchSavedPaymentOther(id) {
  const params = new URLSearchParams({ sourceType: 'Other' })
  if (id != null) params.set('id', String(id))
  try {
    const res = await apiFetch(`/finance/payment-purchase?${params}`)
    if (!res.ok) return null
    const data = await res.json().catch(() => null)
    if (!data || typeof data !== 'object' || data.id == null) return null
    return data
  } catch {
    return null
  }
}

function n(v) {
  const x = Number(v)
  return Number.isFinite(x) ? x : 0
}

function toIntId(v) {
  const num = Number(v)
  return Number.isFinite(num) && num > 0 ? num : null
}

function normalizeRemarkForSave(v) {
  const s = String(v ?? '').trim()
  if (!s || s === '-') return ''
  return s
}

function purchaseCategoryLabel(value) {
  if (!value) return ''
  const opt = purchaseCategoryOptions.find((o) => o.value === value)
  return opt?.label || value
}

const purchaseCategoryDropdownSelected = computed(
  () => String(draft.purchaseCategory || '').trim() !== ''
)

function clearAllRowPurchaseCategoryFlags() {
  const sets = [
    grvRows,
    grvExtraRows,
    nsRows,
    nsExtraRows,
    otherRows,
    otherExtraRows
  ]
  for (const rows of sets) {
    for (const row of rows.value) {
      if (row.usePurchaseCategory) {
        row.usePurchaseCategory = false
        row.purchaseCategory = ''
      }
    }
  }
}

function onRowPurchaseCategoryToggle(row) {
  if (!purchaseCategoryDropdownSelected.value) {
    row.usePurchaseCategory = false
    row.purchaseCategory = ''
    return
  }
  if (row.usePurchaseCategory) {
    row.purchaseCategory = draft.purchaseCategory || ''
  } else {
    row.purchaseCategory = ''
  }
}

function onRowTaxableToggle(row) {
  row.taxable = row.isTaxable ? 'Yes' : 'No'
  if (row?.isManual && usesTaxableAdditionalBreakdown()) {
    row.vat = row.isTaxable ? companyTaxPct.value : 0
  }
}

function lineToPayload(row, mode) {
  const isGrv = mode === 'grv'
  const isOther = mode === 'other'
  const total = isGrv || isOther ? n(row.totalPrice) : n(row.total)
  return {
    lineNo: row.no,
    isManual: !!row.isManual,
    lineKey: String(row.key || ''),
    prNo: row.prNo && row.prNo !== '-' ? String(row.prNo) : '',
    supplierId: toIntId(row.supplierId),
    itemId: resolveRowItemId(row),
    productId: resolveRowProductId(row),
    chartOfAccountId: toIntId(row.chartOfAccountId),
    grvItemId: isGrv ? toIntId(row.grvItemId) : null,
    nsItemId: !isGrv && !isOther ? toIntId(row.nsItemId) : null,
    grvId: isGrv ? toIntId(row.grvId) : null,
    nsId: !isGrv && !isOther ? toIntId(row.nsId) : null,
    itemCode: displayRowCode(row),
    category: row.isManual ? String(row.accountType || row.category || '') : String(row.category || ''),
    dept: String(row.dept || ''),
    itemName: row.isManual || isOther
      ? String(row.item || '').trim()
      : String(row.item || row.accountName || ''),
    volume: row.volume != null ? String(row.volume) : '',
    qty: row.isManual ? parseManualNum(row.qty) : n(row.qty),
    unitPrice: row.isManual ? parseManualNum(row.unitPrice) : n(row.unitPrice),
    totalPrice: total,
    expiry: String(row.expiry || ''),
    taxable: String(row.taxable || ''),
    isTaxable: !!row.isTaxable,
    vat: n(row.vat),
    remark: normalizeRemarkForSave(row.remark),
    isProduct: !!row.isProduct,
    usePurchaseCategory: !!row.usePurchaseCategory,
    purchaseCategory: row.usePurchaseCategory ? String(row.purchaseCategory || '').trim() : '',
    purchaseType: normalizePurchaseType(row.purchaseType, row, mode)
  }
}

function rowFromSavedLine(line, mode) {
  const isGrv = mode === 'grv'
  const isOther = mode === 'other'
  const base = isGrv ? createEmptyGrvRow() : isOther ? createEmptyOtherRow() : createEmptyNsRow()
  base.key = line.lineKey || nextManualKey(mode)
  base.isManual = !!line.isManual
  base.itemId = line.itemId ?? null
  base.productId = line.productId ?? null
  base.itemCode = line.itemCode || ''
  base.chartOfAccountId = line.chartOfAccountId != null ? String(line.chartOfAccountId) : ''
  base.accountCode = ''
  base.accountName = ''
  base.accountType = line.category || ''
  base.category = line.category || ''
  base.prNo = line.prNo || '-'
  base.supplierId = line.supplierId ?? null
  base.supplier = String(line.supplierName || line.supplier || '').trim() || '-'
  base.grvItemId = line.grvItemId ?? null
  base.nsItemId = line.nsItemId ?? null
  base.grvId = line.grvId ?? null
  base.nsId = line.nsId ?? null
  base.item = line.itemName || ''
  base.isProduct = !!line.isProduct
  base.qty = line.isManual ? String(line.qty ?? '') : n(line.qty)
  base.volume = line.volume || (line.isManual ? '' : '-')
  base.unitPrice = line.isManual ? String(line.unitPrice ?? '') : n(line.unitPrice)
  if (isGrv || isOther) {
    base.grvNo = isOther ? '—' : line.grvNumber || draft.grvNumber || '-'
    base.totalPrice = n(line.totalPrice)
    base.expiry = line.expiry || ''
    base.remark = line.remark || ''
  } else {
    base.nsNo = line.nsNumber || draft.nonStockableNumber || '-'
    base.dept = line.dept || '-'
    base.total = n(line.totalPrice)
    base.remark = line.remark || ''
  }
  base.taxable = line.taxable || (line.isTaxable ? 'Yes' : 'No')
  base.isTaxable = !!line.isTaxable
  base.vat = n(line.vat)
  base.usePurchaseCategory = !!line.usePurchaseCategory
  base.purchaseCategory = line.purchaseCategory || ''
  base.purchaseType = normalizePurchaseType(line.purchaseType, base, mode)
  if (line.isManual) {
    applyChartAccountToManualRow(base, base.chartOfAccountId)
    if (isGrv || isOther) syncManualGrvTotals(base)
    else syncManualNsTotals(base)
  }
  return base
}

async function applySavedRecord(saved) {
  if (!saved) return
  savedPaymentId.value = saved.id
  if (saved.grvNumber) draft.grvNumber = saved.grvNumber
  if (saved.nonStockableNumber) draft.nonStockableNumber = saved.nonStockableNumber
  draft.paymentType = saved.paymentType || draft.paymentType
  draft.sellerTin = saved.sellerTin || ''
  draft.sellerName = saved.sellerName || ''
  draft.receiptNumber = saved.receiptNumber || ''
  draft.receiptDate = saved.receiptDate || ''
  draft.calendarType = saved.calendarType || ''
  draft.mrc = saved.mrc || ''
  draft.purchaseCategory = saved.purchaseCategory || ''
  cpvVoucherNo.value = saved.cpvNumber || cpvVoucherNo.value

  let mode = 'grv'
  if (saved.sourceType === 'ns') mode = 'ns'
  else if (String(saved.sourceType || '').toLowerCase() === 'other') mode = 'other'
  if (mode === 'other') {
    otherModeActive.value = true
    if (!isPcpvPaymentType()) {
      draft.grvNumber = ''
      draft.nonStockableNumber = ''
    }
  }
  tableMode.value = mode
  const lines = Array.isArray(saved.lines) ? saved.lines : []
  const main = []
  const extra = []
  for (const line of lines) {
    const row = rowFromSavedLine(line, mode)
    if (row.isManual || mode === 'other') extra.push(row)
    else main.push(row)
  }
  if (mode === 'grv') {
    grvRows.value = main
    grvExtraRows.value = extra
  } else if (mode === 'other') {
    otherRows.value = []
    otherExtraRows.value = extra.length ? extra : [createEmptyOtherRow()]
  } else {
    nsRows.value = main
    nsExtraRows.value = extra
  }
  setCpvPreparedByFromSaved(saved)
  await nextTick()
  cpvFormRef.value?.setFormState({
    amountInWords: saved.amountInWords,
    purposeOfPayment: saved.purposeOfPayment,
    chequeNo: saved.chequeNo,
    bankId: saved.bankId,
    bankPayments: saved.bankPayments,
    preparedDate: saved.preparedDate
  })
}

/** Other: persist grand total like GRV/NS (base + tax); no separate additional amount. */
function getOtherPersistTotals() {
  const summary = summarizeRows(otherAllRows.value, 'totalPrice')
  return {
    baseAmount: summary.baseAmount,
    taxableAmount: summary.taxableAmount,
    taxPct: summary.vatPct,
    taxAmount: summary.taxAmount,
    grandTotal: summary.grandTotal,
    additionalAmount: 0
  }
}

function buildSavePayload() {
  const mode = tableMode.value
  const isGrv = mode === 'grv'
  const isOther = mode === 'other'
  const allRows = isGrv ? grvAllRows.value : isOther ? otherAllRows.value : nsAllRows.value
  const summary = isGrv ? grvSummary.value : isOther ? otherSummary.value : nsSummary.value
  const otherTotals = isOther ? getOtherPersistTotals() : null
  const category = isGrv ? grvCategoryBreakdown.value : isOther ? otherCategoryBreakdown.value : nsCategoryBreakdown.value
  const withholding = isGrv ? grvWithholding.value : isOther ? otherWithholding.value : nsWithholding.value
  const additionalCategory = isGrv
    ? grvAdditionalCategoryBreakdown.value
    : isOther
      ? { serviceBase: 0, goodsBase: 0 }
      : nsAdditionalCategoryBreakdown.value
  const additionalWithholding = isGrv
    ? grvAdditionalWithholding.value
    : isOther
      ? ZERO_WITHHOLDING
      : nsAdditionalWithholding.value
  const additional = isGrv ? grvAdditionalAmount.value : isOther ? otherAdditionalAmount.value : nsAdditionalAmount.value
  const extraRows = isGrv ? grvExtraRows.value : isOther ? otherExtraRows.value : nsExtraRows.value
  const amountField = isGrv || isOther ? 'totalPrice' : 'total'
  const additionalTaxFields =
    isGrv || mode === 'ns'
      ? buildGrvNsAdditionalTaxFields(extraRows, amountField)
      : { taxableAdditionalAmount: 0, additionalTax: 0, additionalGrandTotal: 0 }
  const cpv = cpvFormRef.value?.getFormState?.() || {}
  const sig = cpvSignatureSaveFields()

  return {
    id: savedPaymentId.value,
    sourceType: isOther ? 'Other' : mode,
    grvNumber: isGrv ? resolveFullGrvNumber() : null,
    nonStockableNumber: mode === 'ns' ? resolveFullNsNumber() : null,
    paymentType: String(draft.paymentType || '').trim(),
    sellerTin: String(draft.sellerTin || '').trim(),
    sellerName: String(draft.sellerName || '').trim(),
    receiptNumber: String(draft.receiptNumber || '').trim(),
    receiptDate: draft.receiptDate || null,
    calendarType: String(draft.calendarType || '').trim(),
    mrc: String(draft.mrc || '').trim(),
    purchaseCategory: String(draft.purchaseCategory || '').trim(),
    cpvNumber: cpvVoucherNo.value,
    amountInWords: cpv.amountInWords,
    purposeOfPayment: cpv.purposeOfPayment,
    amountInFigures: cpvAmountInFigures.value,
    chequeNo: cpv.chequeNo,
    bankId: cpv.bankId,
    bankPayments: cpv.bankPayments,
    bankAmountDifference: n(cpv.bankAmountDifference),
    preparedByUserId: sig.preparedByUserId,
    preparedBy: sig.preparedBy,
    preparedDate: sig.preparedDate,
    checkedBy: sig.checkedBy,
    checkedDate: sig.checkedDate,
    approvedBy: sig.approvedBy,
    approvedDate: sig.approvedDate,
    receivedBy: sig.receivedBy,
    receivedDate: sig.receivedDate,
    baseAmount: isOther ? otherTotals.baseAmount : summary.baseAmount,
    taxableAmount: isOther ? otherTotals.taxableAmount : summary.taxableAmount,
    taxPct: isOther ? otherTotals.taxPct : summary.vatPct,
    taxAmount: isOther ? otherTotals.taxAmount : summary.taxAmount,
    grandTotal: isOther ? otherTotals.grandTotal : summary.grandTotal,
    additionalAmount: isOther ? otherTotals.additionalAmount : additional,
    taxableAdditionalAmount: additionalTaxFields.taxableAdditionalAmount,
    additionalTax: additionalTaxFields.additionalTax,
    additionalGrandTotal: additionalTaxFields.additionalGrandTotal,
    serviceAmount: showWithholdingTotals() ? category.serviceBase : 0,
    goodsAmount: showWithholdingTotals() ? category.goodsBase : 0,
    withholdingAmount: showWithholdingTotals() ? combinedWithholdingTotal(withholding) : 0,
    additionalServiceAmount: showWithholdingTotals() ? additionalCategory.serviceBase : 0,
    additionalGoodsAmount: showWithholdingTotals() ? additionalCategory.goodsBase : 0,
    additionalWithholdingAmount: showWithholdingTotals() ? combinedWithholdingTotal(additionalWithholding) : 0,
    lines: allRows.map((row) => lineToPayload(row, mode))
  }
}

function isCpvPaymentType() {
  return String(draft.paymentType || '').trim() === PAYMENT_TYPE_CPV
}

/** Other: footer shows base + tax as grand total and hides additional amount row. */
function isOtherTotalsDisplay() {
  return tableMode.value === 'other'
}

function otherGrandTotalFromSummary(summary) {
  return n(summary?.baseAmount) + n(summary?.taxAmount)
}

function isDirectPaymentType() {
  return String(draft.paymentType || '').trim() === PAYMENT_TYPE_DIRECT
}

function isSingleVoucherPaymentType() {
  return isCpvPaymentType() || isDirectPaymentType()
}

function showReceiptMetaFields() {
  return !isDirectPaymentType()
}

function requireReceiptDate() {
  if (!showReceiptMetaFields()) return true
  const val = String(draft.receiptDate || '').trim()
  if (val) return true
  showToast('Receipt date is required.', 'error')
  return false
}

function rowRequiresAccountSelection(row, mode) {
  if (!row || typeof row !== 'object') return false
  return mode === 'other' || !!row.isManual
}

function requireAccountForDynamicRows(rows, mode) {
  for (const row of rows || []) {
    if (!rowRequiresAccountSelection(row, mode)) continue
    if (toIntId(row.chartOfAccountId) != null) continue
    const rowNo = row.no != null ? row.no : ''
    const rowLabel = rowNo !== '' ? `Row ${rowNo}` : 'A dynamic row'
    showToast(`${rowLabel}: Account selection is required.`, 'error')
    return false
  }
  return true
}

function requireAccountForPcpvDocuments(documents) {
  for (const doc of documents || []) {
    const mode = String(doc?.sourceType || '').toLowerCase() === 'other'
      ? 'other'
      : String(doc?.sourceType || '').trim()
    const lines = Array.isArray(doc?.lines) ? doc.lines : []
    for (const line of lines) {
      const needsAccount = mode === 'other' || !!line?.isManual
      if (!needsAccount) continue
      if (toIntId(line?.chartOfAccountId) != null) continue
      const rowNo = line?.lineNo != null ? line.lineNo : ''
      const rowLabel = rowNo !== '' ? `Row ${rowNo}` : 'A dynamic row'
      showToast(`${rowLabel}: Account selection is required.`, 'error')
      return false
    }
  }
  return true
}

function showWithholdingTotals() {
  return isCpvPaymentType() && !isCompanySol.value
}

function isPcpvPaymentType() {
  return String(draft.paymentType || '').trim() === PAYMENT_TYPE_PCPV
}

/** GRV/NS manual (+) rows: P.C.P.V and Direct Payment apply tax on taxable additional lines. */
function usesTaxableAdditionalBreakdown() {
  return (isPcpvPaymentType() || isDirectPaymentType()) && tableMode.value !== 'other'
}

function computeManualAdditionalTaxBreakdown(extraRows, amountField) {
  const additionalTaxPct = companyTaxPct.value
  const additionalAmount = sumManualLineAmount(extraRows, amountField)
  let taxableAdditionalAmt = 0
  for (const row of extraRows || []) {
    if (!row?.isManual || !row?.isTaxable) continue
    const amt = Number(row[amountField] || 0)
    if (Number.isFinite(amt)) taxableAdditionalAmt += amt
  }
  const additionalTax = taxableAdditionalAmt * (additionalTaxPct / 100)
  const additionalGrandTotal = additionalAmount + additionalTax
  return {
    taxableAdditionalAmt,
    additionalTax,
    additionalTaxPct,
    additionalGrandTotal,
    hasTaxableManualExtra: taxableAdditionalAmt > 0
  }
}

function computeAdditionalCategoryBreakdown(extraRows, amountField) {
  return categorizeBaseAmount(
    (extraRows || []).filter((r) => !!r?.isManual),
    amountField
  )
}

function buildGrvNsAdditionalTaxFields(extraRows, amountField) {
  // CPV shows taxable additional + additional tax in the footer; persist the same displayed values.
  if (isCpvPaymentType() && tableMode.value !== 'other') {
    const breakdown = computeManualAdditionalTaxBreakdown(extraRows, amountField)
    return {
      taxableAdditionalAmount: breakdown.taxableAdditionalAmt,
      additionalTax: breakdown.additionalTax,
      additionalGrandTotal: breakdown.additionalGrandTotal
    }
  }
  if (!usesTaxableAdditionalBreakdown()) {
    const additionalAmount = sumManualLineAmount(extraRows, amountField)
    return {
      taxableAdditionalAmount: 0,
      additionalTax: 0,
      additionalGrandTotal: additionalAmount
    }
  }
  const breakdown = computeManualAdditionalTaxBreakdown(extraRows, amountField)
  return {
    taxableAdditionalAmount: breakdown.taxableAdditionalAmt,
    additionalTax: breakdown.additionalTax,
    additionalGrandTotal: breakdown.additionalGrandTotal
  }
}

function payableAdditionalAmountForMode(mode) {
  if (mode === 'grv') {
    if (usesTaxableAdditionalBreakdown()) {
      return computeManualAdditionalTaxBreakdown(grvExtraRows.value, 'totalPrice').additionalGrandTotal
    }
    return n(grvAdditionalAmount.value)
  }
  if (mode === 'ns') {
    if (usesTaxableAdditionalBreakdown()) {
      return computeManualAdditionalTaxBreakdown(nsExtraRows.value, 'total').additionalGrandTotal
    }
    return n(nsAdditionalAmount.value)
  }
  return 0
}

function docPayableTotal(mode) {
  if (mode === 'grv') {
    return n(grvSummary.value.grandTotal) + payableAdditionalAmountForMode('grv')
  }
  if (mode === 'ns') {
    return n(nsSummary.value.grandTotal) + payableAdditionalAmountForMode('ns')
  }
  if (mode === 'other') {
    return n(otherSummary.value.grandTotal)
  }
  return 0
}

function snapshotDraftReceipt() {
  return {
    sellerTin: draft.sellerTin,
    sellerName: draft.sellerName,
    receiptNumber: draft.receiptNumber,
    receiptDate: draft.receiptDate,
    calendarType: draft.calendarType,
    mrc: draft.mrc,
    purchaseCategory: draft.purchaseCategory
  }
}

function buildDocumentSavePayload(mode, rowsContext = null) {
  const isGrv = mode === 'grv'
  const isOther = mode === 'other'
  let mainRows
  let extraRows
  let grvNumber
  let nonStockableNumber

  if (rowsContext) {
    mainRows = rowsContext.mainRows || []
    extraRows = rowsContext.extraRows || []
    grvNumber = isGrv ? rowsContext.grvNumber : null
    nonStockableNumber = mode === 'ns' ? rowsContext.nonStockableNumber : null
  } else {
    mainRows = isGrv ? grvRows.value : isOther ? otherRows.value : nsRows.value
    extraRows = isGrv ? grvExtraRows.value : isOther ? otherExtraRows.value : nsExtraRows.value
    grvNumber = isGrv ? resolveFullGrvNumber() : null
    nonStockableNumber = mode === 'ns' ? resolveFullNsNumber() : null
  }

  const allRows = [...mainRows, ...extraRows]
  if (!allRows.length) return null

  const amountField = isGrv ? 'totalPrice' : isOther ? 'totalPrice' : 'total'
  const summary = summarizeRows(mainRows, amountField)
  const otherTotals = isOther ? getOtherPersistTotalsForRows(allRows) : null
  const mainCategory = isOther ? { serviceBase: 0, goodsBase: 0 } : categorizeBaseAmount(mainRows, amountField)
  const additionalCategory = isOther ? { serviceBase: 0, goodsBase: 0 } : computeAdditionalCategoryBreakdown(extraRows, amountField)
  const withTin = sellerTinFilled()
  const settings = getWithholdingSettings()
  const mainWithholding = isOther ? ZERO_WITHHOLDING : computeWithholding(mainCategory, settings, withTin)
  const extraWithholding = isOther ? ZERO_WITHHOLDING : computeWithholding(additionalCategory, settings, withTin)
  const additional = isGrv
    ? sumManualLineAmount(extraRows, 'totalPrice')
    : isOther
      ? otherTotals.additionalAmount
      : sumManualLineAmount(extraRows, 'total')
  const additionalTaxFields =
    isGrv || mode === 'ns'
      ? buildGrvNsAdditionalTaxFields(extraRows, amountField)
      : { taxableAdditionalAmount: 0, additionalTax: 0, additionalGrandTotal: 0 }

  return {
    sourceType: isOther ? 'Other' : mode,
    grvNumber,
    nonStockableNumber,
    ...snapshotDraftReceipt(),
    baseAmount: isOther ? otherTotals.baseAmount : summary.baseAmount,
    taxableAmount: isOther ? otherTotals.taxableAmount : summary.taxableAmount,
    taxPct: isOther ? otherTotals.taxPct : summary.vatPct,
    taxAmount: isOther ? otherTotals.taxAmount : summary.taxAmount,
    grandTotal: isOther ? otherTotals.grandTotal : summary.grandTotal,
    additionalAmount: isOther ? otherTotals.additionalAmount : additional,
    taxableAdditionalAmount: additionalTaxFields.taxableAdditionalAmount,
    additionalTax: additionalTaxFields.additionalTax,
    additionalGrandTotal: additionalTaxFields.additionalGrandTotal,
    serviceAmount: showWithholdingTotals() ? mainCategory.serviceBase : 0,
    goodsAmount: showWithholdingTotals() ? mainCategory.goodsBase : 0,
    withholdingAmount: showWithholdingTotals() ? combinedWithholdingTotal(mainWithholding) : 0,
    additionalServiceAmount: showWithholdingTotals() ? additionalCategory.serviceBase : 0,
    additionalGoodsAmount: showWithholdingTotals() ? additionalCategory.goodsBase : 0,
    additionalWithholdingAmount: showWithholdingTotals() ? combinedWithholdingTotal(extraWithholding) : 0,
    lines: allRows.map((row) => lineToPayload(row, mode))
  }
}

function getOtherPersistTotalsForRows(rows) {
  const summary = summarizeRows(rows, 'totalPrice')
  return {
    baseAmount: summary.baseAmount,
    taxableAmount: summary.taxableAmount,
    taxPct: summary.vatPct,
    taxAmount: summary.taxAmount,
    grandTotal: summary.grandTotal,
    additionalAmount: 0
  }
}

function docPayableFromRows(mode, mainRows, extraRows = []) {
  const isGrv = mode === 'grv'
  const isOther = mode === 'other'
  const amountField = isGrv ? 'totalPrice' : isOther ? 'totalPrice' : 'total'
  if (isOther) {
    const summaryAll = summarizeRows([...mainRows, ...extraRows], 'totalPrice')
    return n(summaryAll.grandTotal)
  }
  const summary = summarizeRows(mainRows, amountField)
  if ((mode === 'grv' || mode === 'ns') && usesTaxableAdditionalBreakdown()) {
    const breakdown = computeManualAdditionalTaxBreakdown(extraRows, amountField)
    return n(summary.grandTotal) + n(breakdown.additionalGrandTotal)
  }
  const additional = sumManualLineAmount(extraRows, amountField)
  return n(summary.grandTotal) + n(additional)
}

function buildPcpvDocQueueEntry(info) {
  const { mode, key, label, grvNumber, nonStockableNumber, mainRows, extraRows = [] } = info
  const payload = buildDocumentSavePayload(mode, {
    mainRows,
    extraRows,
    grvNumber: grvNumber || null,
    nonStockableNumber: nonStockableNumber || null
  })
  return {
    key,
    label,
    mode,
    grvNumber: grvNumber || '',
    nonStockableNumber: nonStockableNumber || '',
    mainRows: [...mainRows],
    extraRows: [...extraRows],
    docPayableTotal: docPayableFromRows(mode, mainRows, extraRows),
    payload
  }
}

function listPcpvDocumentsFromTable() {
  const mode = tableMode.value
  if (!mode) return []

  if (mode === 'grv') {
    const grouped = []
    for (const row of grvRows.value) {
      const no = String(row.grvNo || '').trim()
      if (!no || no === '-') continue
      const key = pcpvDocKey('grv', no, null)
      let entry = grouped.find((g) => g.key === key)
      if (!entry) {
        entry = {
          key,
          label: no,
          mode: 'grv',
          grvNumber: no,
          nonStockableNumber: '',
          mainRows: [],
          extraRows: []
        }
        grouped.push(entry)
      }
      entry.mainRows.push(row)
    }
    if (grouped.length === 1) {
      grouped[0].extraRows = [...grvExtraRows.value]
    }
    return grouped.map((info) => buildPcpvDocQueueEntry(info))
  }

  if (mode === 'ns') {
    const grouped = []
    for (const row of nsRows.value) {
      const no = String(row.nsNo || '').trim()
      if (!no || no === '-') continue
      const key = pcpvDocKey('ns', null, no)
      let entry = grouped.find((g) => g.key === key)
      if (!entry) {
        entry = {
          key,
          label: no,
          mode: 'ns',
          grvNumber: '',
          nonStockableNumber: no,
          mainRows: [],
          extraRows: []
        }
        grouped.push(entry)
      }
      entry.mainRows.push(row)
    }
    if (grouped.length === 1) {
      grouped[0].extraRows = [...nsExtraRows.value]
    }
    return grouped.map((info) => buildPcpvDocQueueEntry(info))
  }

  if (mode === 'other') {
    const currentMain = [...otherRows.value]
    const currentExtra = [...otherExtraRows.value]
    if (!currentMain.length && !currentExtra.length) return []
    const key = pcpvActiveOtherDocKey.value || nextPcpvOtherDocKey()
    const otherIndex = String(key).split(':')[1] || '1'
    const label = otherIndex === '1' ? 'Other' : `Other ${otherIndex}`
    return [
      buildPcpvDocQueueEntry({
        key,
        label,
        mode: 'other',
        grvNumber: '',
        nonStockableNumber: '',
        mainRows: currentMain,
        extraRows: currentExtra
      })
    ]
  }

  return []
}

function pcpvDocKey(mode, grvNo, nsNo, otherId) {
  if (mode === 'other') {
    const id = otherId != null ? String(otherId) : String(savedPaymentId.value || 'new')
    return `other:${id}`
  }
  const g = extractDocSuffix(grvNo)
  const ns = extractDocSuffix(nsNo)
  return mode === 'grv' ? `grv:${g}` : `ns:${ns}`
}

function nextPcpvOtherDocKey() {
  const count = pcpvQueue.value.filter((d) => d.mode === 'other').length
  return pcpvDocKey('other', null, null, count + 1)
}

function pcpvQueueHasDoc(mode, grvNo, nsNo) {
  const key = pcpvDocKey(mode, grvNo, nsNo)
  if (pcpvQueue.value.some((d) => d.key === key)) return true
  if (mode === 'grv') {
    return grvRows.value.some((row) => matchesDocNo(row.grvNo, grvNo))
  }
  if (mode === 'ns') {
    return nsRows.value.some((row) => matchesDocNo(row.nsNo, nsNo))
  }
  if (mode === 'other') {
    const key = pcpvActiveOtherDocKey.value || nextPcpvOtherDocKey()
    if (pcpvQueue.value.some((d) => d.key === key)) return true
    return otherRows.value.length > 0 || otherExtraRows.value.length > 0
  }
  return false
}

/** Clears receipt / seller / purchase detail fields; keeps toolbar filters (GRV, NS, payment type). */
function clearReceiptAndDetailFields() {
  draft.sellerTin = ''
  draft.sellerName = ''
  draft.receiptNumber = ''
  draft.receiptDate = ''
  draft.calendarType = ''
  draft.mrc = ''
  draft.purchaseCategory = ''
  cpvBankError.value = false
  cpvFormRef.value?.setFormState?.({})
}

function clearPcpvToolbarInputs() {
  draft.grvNumber = ''
  draft.nonStockableNumber = ''
  clearReceiptAndDetailFields()
}

function clearPcpvWorkingForm() {
  clearPcpvToolbarInputs()
  grvRows.value = []
  nsRows.value = []
  grvExtraRows.value = []
  nsExtraRows.value = []
  otherRows.value = []
  otherExtraRows.value = []
  pcpvActiveOtherDocKey.value = ''
  tableMode.value = null
}

function clearCurrentTableRowsOnly() {
  const mode = tableMode.value
  if (mode === 'grv') {
    grvRows.value = []
    grvExtraRows.value = []
  } else if (mode === 'ns') {
    nsRows.value = []
    nsExtraRows.value = []
  } else if (mode === 'other') {
    otherRows.value = []
    otherExtraRows.value = []
    pcpvActiveOtherDocKey.value = ''
  }
}

/** Move the current table document(s) into the P.C.P.V queue before loading the next generate. */
function commitCurrentTableToPcpvQueue() {
  if (!isPcpvPaymentType() || !tableMode.value) return true
  const docs = listPcpvDocumentsFromTable()
  if (!docs.length) return true
  if (!requireReceiptDate()) return false
  for (const doc of docs) {
    const idx = pcpvQueue.value.findIndex((d) => d.key === doc.key)
    const entry = {
      ...doc,
      mainRows: [...(doc.mainRows || [])],
      extraRows: [...(doc.extraRows || [])]
    }
    if (idx >= 0) pcpvQueue.value[idx] = entry
    else pcpvQueue.value.push(entry)
  }
  clearCurrentTableRowsOnly()
  return true
}

function assignPcpvGrvRows(newRows) {
  grvRows.value = newRows
  grvExtraRows.value = []
  tableMode.value = 'grv'
}

function assignPcpvNsRows(newRows) {
  nsRows.value = newRows
  nsExtraRows.value = []
  tableMode.value = 'ns'
}

function applyPcpvBatchLoad(saved) {
  pcpvLoadedBatch.value = saved
  pcpvQueue.value = []
  pcpvSelectedAddedDocKey.value = ''
  pcpvActiveOtherDocKey.value = ''
  draft.paymentType = PAYMENT_TYPE_PCPV
  cpvVoucherNo.value = saved.cpvNumber || ''
  clearPcpvWorkingForm()
}

function resetPcpvSession() {
  pcpvQueue.value = []
  pcpvSelectedAddedDocKey.value = ''
  pcpvActiveOtherDocKey.value = ''
  pcpvLoadedBatch.value = null
  clearCpvPreparedBy()
}

function currentPcpvDocumentKey() {
  if (!tableMode.value) return null
  const mode = tableMode.value
  if (mode === 'grv') return pcpvDocKey('grv', resolveFullGrvNumber(), null)
  if (mode === 'ns') return pcpvDocKey('ns', null, resolveFullNsNumber())
  return pcpvDocKey('other', null, null)
}

function collectPcpvDocumentsForSave() {
  if (pcpvAddedDocuments.value.length > 0) {
    return pcpvAddedDocuments.value.map((entry) => ({ ...entry.payload }))
  }
  const documents = pcpvQueue.value.map((entry) => ({ ...entry.payload }))
  const pending = currentTableSavePayload()
  if (pending) {
    const pendingKey = currentPcpvDocumentKey()
    const alreadyQueued = pendingKey && pcpvQueue.value.some((d) => d.key === pendingKey)
    if (!alreadyQueued) documents.push(pending)
  }
  return documents
}

function buildPcpvBatchPayload() {
  const cpv = cpvFormRef.value?.getFormState?.() || {}
  const sig = cpvSignatureSaveFields()
  const batchPayable = pcpvLiveBatchPayableTotal.value
  let batchBase = 0
  let batchTaxable = 0
  let batchTax = 0
  let batchGrand = 0
  let batchAdditional = 0
  let maxTaxPct = 0

  const documents = collectPcpvDocumentsForSave()
  for (const p of documents) {
    batchBase += n(p.baseAmount)
    batchTaxable += n(p.taxableAmount)
    batchTax += n(p.taxAmount)
    batchGrand += n(p.grandTotal)
    batchAdditional += n(p.additionalAmount)
    if (n(p.taxPct) > maxTaxPct) maxTaxPct = n(p.taxPct)
  }

  return {
    paymentType: PAYMENT_TYPE_PCPV,
    cpvNumber: cpvVoucherNo.value,
    amountInWords: cpv.amountInWords,
    purposeOfPayment: cpv.purposeOfPayment,
    amountInFigures: batchPayable,
    chequeNo: cpv.chequeNo,
    bankId: cpv.bankId,
    bankPayments: cpv.bankPayments,
    bankAmountDifference: n(cpv.bankAmountDifference),
    preparedByUserId: sig.preparedByUserId,
    preparedBy: sig.preparedBy,
    preparedDate: sig.preparedDate,
    checkedBy: sig.checkedBy,
    checkedDate: sig.checkedDate,
    approvedBy: sig.approvedBy,
    approvedDate: sig.approvedDate,
    receivedBy: sig.receivedBy,
    receivedDate: sig.receivedDate,
    baseAmount: batchBase,
    taxableAmount: batchTaxable,
    taxPct: maxTaxPct,
    taxAmount: batchTax,
    grandTotal: batchGrand,
    additionalAmount: batchAdditional,
    serviceAmount: 0,
    goodsAmount: 0,
    withholdingAmount: 0,
    documents
  }
}

async function savePcpvBatch() {
  const documents = collectPcpvDocumentsForSave()
  if (!documents.length) {
    showToast('Add at least one GRV, non-stockable, or Other document.', 'error')
    return
  }
  if (!requireAccountForPcpvDocuments(documents)) return
  if (!requireReceiptDate()) return
  if (!cpvHasValidBank()) {
    cpvBankError.value = true
    showToast(cpvBankValidationMessage() || 'Please enter at least one bank amount on the cheque payment voucher.', 'error')
    return
  }
  cpvBankError.value = false
  isSaving.value = true
  try {
    const body = buildPcpvBatchPayload()
    const res = await apiFetch('/finance/payment-purchase', {
      method: 'POST',
      body: JSON.stringify(body)
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to save P.C.P.V batch.', 'error')
      return
    }
    const saved = await res.json().catch(() => null)
    showToast('P.C.P.V saved successfully.', 'success')
    resetPcpvSession()
    resetAfterCpvSave()
    await loadAvailableDocNumbers()
    if (saved?.cpvNumber) cpvVoucherNo.value = saved.cpvNumber
  } catch {
    showToast('Failed to save P.C.P.V batch.', 'error')
  } finally {
    isSaving.value = false
  }
}

/** Reset UI after a successful C.P.V save (P.C.P.V keeps data for future work). */
function onCpvBankSelected() {
  cpvBankError.value = false
}

function onCpvBankLimitExceeded(message) {
  showToast(message || 'Total bank amounts cannot exceed Amount in figures.', 'error')
}

function cpvHasValidBank() {
  return cpvFormRef.value?.hasValidBank?.() === true
}

function cpvBankValidationMessage() {
  const msg = cpvFormRef.value?.getBankValidationMessage?.()
  return String(msg || '').trim()
}

function resetAfterCpvSave() {
  clearCpvPreparedBy()
  draft.grvNumber = ''
  draft.nonStockableNumber = ''
  draft.paymentType = ''
  clearReceiptAndDetailFields()
  grvRows.value = []
  nsRows.value = []
  otherRows.value = []
  grvExtraRows.value = []
  nsExtraRows.value = []
  otherExtraRows.value = []
  otherModeActive.value = false
  tableMode.value = null
  cpvVoucherNo.value = ''
  savedPaymentId.value = null
}

async function savePayment() {
  if (!isSingleVoucherPaymentType()) return
  if (isSaving.value || !tableMode.value) return
  const mode = tableMode.value
  const allRows = mode === 'grv' ? grvAllRows.value : mode === 'other' ? otherAllRows.value : nsAllRows.value
  if (!allRows.length) {
    showToast('No lines to save.', 'error')
    return
  }
  if (!String(draft.paymentType || '').trim()) {
    showToast('Please select a payment type.', 'error')
    return
  }
  if (!requireAccountForDynamicRows(allRows, mode)) return
  if (!requireReceiptDate()) return
  if (!cpvHasValidBank()) {
    cpvBankError.value = true
    showToast(cpvBankValidationMessage() || 'Please enter at least one bank amount on the cheque payment voucher.', 'error')
    return
  }
  cpvBankError.value = false

  const savedAsCpv = isSingleVoucherPaymentType()
  isSaving.value = true
  try {
    const body = buildSavePayload()
    const res = await apiFetch('/finance/payment-purchase', {
      method: 'POST',
      body: JSON.stringify(body)
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err.message || 'Failed to save payment purchase.', 'error')
      return
    }
    const saved = await res.json().catch(() => null)
    const isUpdate = savedPaymentId.value != null
    showToast(
      isUpdate ? 'Payment purchase updated successfully.' : 'Payment purchase saved successfully.',
      'success'
    )
    if (savedAsCpv) {
      resetAfterCpvSave()
      await loadAvailableDocNumbers()
    } else {
      if (saved?.id != null) savedPaymentId.value = saved.id
      if (saved?.cpvNumber) cpvVoucherNo.value = saved.cpvNumber
    }
  } catch {
    showToast('Failed to save payment purchase.', 'error')
  } finally {
    isSaving.value = false
  }
}

const cpvPaidTo = computed(() => {
  if (tableMode.value === 'grv') {
    const name = grvRows.value?.[0]?.supplier
    return String(name || '').trim() || '-'
  }
  if (tableMode.value === 'ns') {
    const name = nsRows.value?.[0]?.supplier
    return String(name || '').trim() || '-'
  }
  if (tableMode.value === 'other') {
    return 'Other'
  }
  return '-'
})

const cpvAmountInFigures = computed(() => {
  if (isPcpvPaymentType()) {
    if (pcpvLoadedBatch.value?.batchSummary) {
      return n(pcpvLoadedBatch.value.batchSummary.batchPayableTotal)
    }
    return pcpvLiveBatchPayableTotal.value
  }
  void withholdingTinTick.value
  if (tableMode.value === 'grv') {
    const extraPayable = isCpvPaymentType()
      ? Number(grvAdditionalTaxBreakdown.value?.additionalGrandTotal || 0)
      : usesTaxableAdditionalBreakdown()
        ? computeManualAdditionalTaxBreakdown(grvExtraRows.value, 'totalPrice').additionalGrandTotal
        : Number(grvAdditionalAmount.value || 0)
    const total =
      Number(grvSummary.value.grandTotal || 0) +
      extraPayable -
      Number(combinedWithholdingTotal(grvWithholding.value)) -
      (isCpvPaymentType() ? Number(combinedWithholdingTotal(grvAdditionalWithholding.value)) : 0)
    return Number.isFinite(total) ? total : 0
  }
  if (tableMode.value === 'ns') {
    const extraPayable = isCpvPaymentType()
      ? Number(nsAdditionalTaxBreakdown.value?.additionalGrandTotal || 0)
      : usesTaxableAdditionalBreakdown()
        ? computeManualAdditionalTaxBreakdown(nsExtraRows.value, 'total').additionalGrandTotal
        : Number(nsAdditionalAmount.value || 0)
    const total =
      Number(nsSummary.value.grandTotal || 0) +
      extraPayable -
      Number(combinedWithholdingTotal(nsWithholding.value)) -
      (isCpvPaymentType() ? Number(combinedWithholdingTotal(nsAdditionalWithholding.value)) : 0)
    return Number.isFinite(total) ? total : 0
  }
  if (tableMode.value === 'other') {
    const grandTotal = otherGrandTotalFromSummary(otherSummary.value)
    if (isCpvPaymentType()) {
      const total = grandTotal - Number(combinedWithholdingTotal(otherWithholding.value))
      return Number.isFinite(total) ? total : 0
    }
    return grandTotal
  }
  return 0
})

const grvSummary = computed(() => summarizeRows(grvRows.value, 'totalPrice'))
const nsSummary = computed(() => summarizeRows(nsRows.value, 'total'))
const otherSummary = computed(() => summarizeRows(otherAllRows.value, 'totalPrice'))

function sumManualLineAmount(rows, amountField) {
  return (rows || []).reduce((sum, r) => {
    const n = Number(r[amountField] || 0)
    return Number.isFinite(n) ? sum + n : sum
  }, 0)
}

const grvAdditionalAmount = computed(() => sumManualLineAmount(grvExtraRows.value, 'totalPrice'))
const nsAdditionalAmount = computed(() => sumManualLineAmount(nsExtraRows.value, 'total'))
const otherAdditionalAmount = computed(() => sumManualLineAmount(otherExtraRows.value, 'totalPrice'))

const grvAdditionalTaxBreakdown = computed(() => {
  if (isPcpvPaymentType() && showGrvStyleTable.value) {
    const s = pcpvCombinedSummary.value
    return {
      taxableAdditionalAmt: n(s.taxableAdditionalAmount),
      additionalTax: n(s.additionalTax),
      additionalTaxPct: companyTaxPct.value,
      additionalGrandTotal: n(s.additionalGrandTotal),
      hasTaxableManualExtra: n(s.taxableAdditionalAmount) > 0
    }
  }
  return computeManualAdditionalTaxBreakdown(grvExtraRows.value, 'totalPrice')
})

const nsAdditionalTaxBreakdown = computed(() => {
  if (isPcpvPaymentType() && showGrvStyleTable.value) {
    const s = pcpvCombinedSummary.value
    return {
      taxableAdditionalAmt: n(s.taxableAdditionalAmount),
      additionalTax: n(s.additionalTax),
      additionalTaxPct: companyTaxPct.value,
      additionalGrandTotal: n(s.additionalGrandTotal),
      hasTaxableManualExtra: n(s.taxableAdditionalAmount) > 0
    }
  }
  return computeManualAdditionalTaxBreakdown(nsExtraRows.value, 'total')
})

const otherFooterGrandTotal = computed(() => n(otherSummary.value.grandTotal))
const otherFooterAdditionalAmount = computed(() => 0)

function isServicePurchaseType(purchaseType) {
  return String(purchaseType || '').trim() === PURCHASE_TYPE_SERVICE
}

function categorizeBaseAmount(rows, amountField) {
  let serviceBase = 0
  let goodsBase = 0
  for (const r of rows || []) {
    const amt = Number(r[amountField] || 0)
    if (!Number.isFinite(amt) || amt <= 0) continue
    if (isServicePurchaseType(r.purchaseType)) {
      serviceBase += amt
    } else {
      goodsBase += amt
    }
  }
  return { serviceBase, goodsBase }
}

/** Bumped on Seller TIN or row purchase type change so category/withholding totals recalculate live. */
const withholdingTinTick = ref(0)

function bumpWithholdingRecalc() {
  withholdingTinTick.value += 1
}

const grvCategoryBreakdown = computed(() => {
  void withholdingTinTick.value
  return categorizeBaseAmount(grvRows.value, 'totalPrice')
})
const nsCategoryBreakdown = computed(() => {
  void withholdingTinTick.value
  return categorizeBaseAmount(nsRows.value, 'total')
})
const otherCategoryBreakdown = computed(() => {
  void withholdingTinTick.value
  return categorizeBaseAmount(otherAllRows.value, 'totalPrice')
})
const grvAdditionalCategoryBreakdown = computed(() => {
  void withholdingTinTick.value
  return computeAdditionalCategoryBreakdown(grvExtraRows.value, 'totalPrice')
})
const nsAdditionalCategoryBreakdown = computed(() => {
  void withholdingTinTick.value
  return computeAdditionalCategoryBreakdown(nsExtraRows.value, 'total')
})

function onRowPurchaseTypeChange(row, purchaseType) {
  if (!isPurchaseTypeEditable(row)) {
    row.purchaseType = PURCHASE_TYPE_SERVICE
    return
  }
  const next = String(purchaseType || '').trim()
  if (next === PURCHASE_TYPE_SERVICE || next === PURCHASE_TYPE_GOODS) {
    row.purchaseType = next
  }
  bumpWithholdingRecalc()
}

/** Manual (+) / Other rows and GRV / NS generated lines. */
function isPurchaseTypeEditable() {
  return true
}

function onSellerTinInput() {
  bumpWithholdingRecalc()
}

watch(
  () => draft.sellerTin,
  () => {
    bumpWithholdingRecalc()
  }
)

function sellerTinFilled() {
  return String(draft.sellerTin ?? '').trim() !== ''
}

const withholdingSettings = ref(null)

function normalizeWithholdingSettings(data) {
  if (!data || typeof data !== 'object') return null
  return {
    withTinServiceMoreThan: data.withTinServiceMoreThan ?? '',
    withTinServicePercent: data.withTinServicePercent ?? '',
    withTinGoodsMoreThan: data.withTinGoodsMoreThan ?? '',
    withTinGoodsPercent: data.withTinGoodsPercent ?? '',
    withoutTinServiceMoreThan: data.withoutTinServiceMoreThan ?? '',
    withoutTinServicePercent: data.withoutTinServicePercent ?? '',
    withoutTinGoodsMoreThan: data.withoutTinGoodsMoreThan ?? '',
    withoutTinGoodsPercent: data.withoutTinGoodsPercent ?? ''
  }
}

function readWithholdingSettingsFromStorage() {
  try {
    const raw = localStorage.getItem(WITHHOLDING_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return normalizeWithholdingSettings(parsed)
  } catch {
    return null
  }
}

async function loadWithholdingSettings() {
  try {
    const res = await apiFetch('/finance/withholding')
    if (res.ok) {
      const data = await res.json().catch(() => null)
      const normalized = normalizeWithholdingSettings(data)
      if (normalized) {
        withholdingSettings.value = normalized
        try {
          localStorage.setItem(WITHHOLDING_STORAGE_KEY, JSON.stringify(normalized))
        } catch {
          // ignore storage errors
        }
        return
      }
    }
  } catch {
    // API unavailable — fall back to cached settings if present
  }
  const cached = readWithholdingSettingsFromStorage()
  if (cached) withholdingSettings.value = cached
}

function getWithholdingSettings() {
  return withholdingSettings.value || readWithholdingSettingsFromStorage()
}

function parseWithholdingNum(v) {
  const n = Number(String(v ?? '').replace(/,/g, '').trim())
  return Number.isFinite(n) ? n : 0
}

/** Apply category percent when aggregate base is >= more-than threshold (withholding register). */
function calcWithholdingForBase(baseAmount, moreThanRaw, percentRaw) {
  const base = Number(baseAmount || 0)
  if (!Number.isFinite(base) || base <= 0) return 0
  const pct = parseWithholdingNum(percentRaw)
  if (pct <= 0) return 0
  const moreThanText = String(moreThanRaw ?? '').trim()
  if (moreThanText !== '') {
    const threshold = parseWithholdingNum(moreThanText)
    if (base < threshold) return 0
  }
  return base * (pct / 100)
}

/** Combined withholding = service portion + goods portion (always summed). */
function combinedWithholdingTotal(withholding) {
  return n(withholding?.service) + n(withholding?.goods)
}

function computeWithholding(categoryBreakdown, settings, withTin) {
  if (!settings) {
    return { service: 0, goods: 0, total: 0 }
  }
  const prefix = withTin ? 'withTin' : 'withoutTin'
  // Withholding total = (Service base × service %) + (Goods base × goods %), each only when base >= more-than threshold
  const service = calcWithholdingForBase(
    categoryBreakdown?.serviceBase,
    settings[prefix + 'ServiceMoreThan'],
    settings[prefix + 'ServicePercent']
  )
  const goods = calcWithholdingForBase(
    categoryBreakdown?.goodsBase,
    settings[prefix + 'GoodsMoreThan'],
    settings[prefix + 'GoodsPercent']
  )
  const total = combinedWithholdingTotal({ service, goods })
  return { service, goods, total }
}

const grvWithholding = computed(() => {
  if (isDirectPaymentType() || isCompanySol.value) return ZERO_WITHHOLDING
  void withholdingTinTick.value
  void withholdingSettings.value
  const withTin = sellerTinFilled()
  // NOTE: Main "Withholding" must be computed from the document rows only.
  // Manual/dynamic extra rows belong to "Additional Withholding".
  const breakdown = categorizeBaseAmount(grvRows.value, 'totalPrice')
  return computeWithholding(breakdown, getWithholdingSettings(), withTin)
})
const nsWithholding = computed(() => {
  if (isDirectPaymentType() || isCompanySol.value) return ZERO_WITHHOLDING
  void withholdingTinTick.value
  void withholdingSettings.value
  const withTin = sellerTinFilled()
  // NOTE: Main "Withholding" must be computed from the document rows only.
  // Manual/dynamic extra rows belong to "Additional Withholding".
  const breakdown = categorizeBaseAmount(nsRows.value, 'total')
  return computeWithholding(breakdown, getWithholdingSettings(), withTin)
})
const otherWithholding = computed(() => {
  if (isDirectPaymentType() || isCompanySol.value) return ZERO_WITHHOLDING
  void withholdingTinTick.value
  void withholdingSettings.value
  const withTin = sellerTinFilled()
  const breakdown = categorizeBaseAmount(otherAllRows.value, 'totalPrice')
  return computeWithholding(breakdown, getWithholdingSettings(), withTin)
})
const grvAdditionalWithholding = computed(() => {
  if (isDirectPaymentType() || isCompanySol.value) return ZERO_WITHHOLDING
  void withholdingTinTick.value
  void withholdingSettings.value
  const withTin = sellerTinFilled()
  return computeWithholding(grvAdditionalCategoryBreakdown.value, getWithholdingSettings(), withTin)
})
const nsAdditionalWithholding = computed(() => {
  if (isDirectPaymentType() || isCompanySol.value) return ZERO_WITHHOLDING
  void withholdingTinTick.value
  void withholdingSettings.value
  const withTin = sellerTinFilled()
  return computeWithholding(nsAdditionalCategoryBreakdown.value, getWithholdingSettings(), withTin)
})

let toastTimer = null

function showToast(message, variant = 'success', durationMs = 2200) {
  if (toastTimer) {
    clearTimeout(toastTimer)
    toastTimer = null
  }
  toast.message = message
  toast.variant = variant
  toast.show = true
  toastTimer = window.setTimeout(() => {
    toast.show = false
    toastTimer = null
  }, durationMs)
}

const enabledChartAccounts = computed(() =>
  chartAccounts.value.filter((a) => a.enabled === true || a.enabled === 1)
)

function displayRowCode(row) {
  if (row?.isManual) return manualRowCode(row)
  return String(row.itemCode ?? row.item_code ?? '').trim()
}

function manualRowCode(row) {
  if (!row?.isManual) return ''
  const ic = String(row.itemCode ?? '').trim()
  const ac = String(row.accountCode ?? '').trim()
  if (ic && ac && ic !== ac) return `${ic} / ${ac}`
  return ic || ac
}

function parseManualNum(v) {
  const n = Number(String(v ?? '').replace(/,/g, '').trim())
  return Number.isFinite(n) ? n : 0
}

function syncManualGrvTotals(row) {
  const qty = parseManualNum(row.qty)
  const price = parseManualNum(row.unitPrice)
  row.totalPrice = qty * price
}

function syncManualNsTotals(row) {
  const qty = parseManualNum(row.qty)
  const price = parseManualNum(row.unitPrice)
  row.total = qty * price
}

function applyChartAccountToManualRow(row, chartId) {
  const id = chartId ? Number(chartId) : null
  const acc = enabledChartAccounts.value.find((a) => Number(a.id) === id)
  row.chartOfAccountId = chartId ? String(chartId) : ''
  row.accountCode = acc?.code || ''
  row.accountName = acc?.accountName || ''
  row.accountType = acc?.accountType || ''
  row.category = row.accountType
}

async function onManualGrvAccountChange(row) {
  applyChartAccountToManualRow(row, row.chartOfAccountId)
  syncManualGrvTotals(row)
}

async function onManualNsAccountChange(row) {
  applyChartAccountToManualRow(row, row.chartOfAccountId)
  syncManualNsTotals(row)
}

function onManualGrvQtyPrice(row) {
  syncManualGrvTotals(row)
  bumpWithholdingRecalc()
}

function onManualNsQtyPrice(row) {
  syncManualNsTotals(row)
  bumpWithholdingRecalc()
}

async function loadChartAccounts() {
  if (isLoadingChartAccounts.value) return
  isLoadingChartAccounts.value = true
  try {
    const res = await apiFetch('/finance/chart-of-account')
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      showToast(err?.message || 'Failed to load chart of accounts.', 'error')
      chartAccounts.value = []
      return
    }
    const parsed = await res.json().catch(() => [])
    chartAccounts.value = Array.isArray(parsed) ? parsed : []
  } catch {
    showToast('Failed to load chart of accounts.', 'error')
    chartAccounts.value = []
  } finally {
    isLoadingChartAccounts.value = false
  }
}

onMounted(() => {
  loadChartAccounts()
  loadCpvActiveBanks()
})

watch(showChequePaymentVoucher, (show) => {
  if (show) loadCpvActiveBanks()
})

watch(tableMode, (mode) => {
  if (mode && chartAccounts.value.length === 0) {
    loadChartAccounts()
  }
})

function fmt(x) {
  return n(x).toFixed(2)
}

function normDocNo(v) {
  return String(v || '').trim().toUpperCase()
}

/** Sequence after the prefix hyphen (GRV-0088 → 0088, NS-0021 → 0021). */
function extractDocSuffix(v) {
  const n = normDocNo(v)
  if (!n) return ''
  const dash = n.indexOf('-')
  return dash >= 0 ? n.slice(dash + 1).trim() : n
}

/** Match by suffix only — search may be "0088" or "GRV-0088". */
function matchesDocNo(actual, search) {
  const actualSuffix = extractDocSuffix(actual)
  const searchSuffix = extractDocSuffix(search)
  if (!searchSuffix || !actualSuffix) return false

  if (actualSuffix === searchSuffix) return true

  if (/^\d+$/.test(actualSuffix) && /^\d+$/.test(searchSuffix)) {
    return Number(actualSuffix) === Number(searchSuffix)
  }

  return false
}

function splitCsv(v) {
  if (v == null || v === '') return []
  return String(v)
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '')
}

function parseCsvNums(v) {
  return splitCsv(v)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n))
}

function formatExpiry(v) {
  if (!v) return '-'
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return String(v)
  return d.toISOString().slice(0, 10)
}

function resolveItemCode(item) {
  if (item?.productId || item?.product) {
    return String(item?.product?.code || item?.product?.productCode || '').trim()
  }
  return String(item?.item?.itemCode || item?.item?.item_code || '').trim()
}

function firstPositiveInt(...values) {
  for (const raw of values) {
    const n = Number(raw)
    if (Number.isFinite(n) && n > 0) return n
  }
  return null
}

function resolveLineItemId(item) {
  if (!item || typeof item !== 'object') return null
  return firstPositiveInt(item.itemId, item.item_id, item.item?.id)
}

function resolveLineProductId(item) {
  if (!item || typeof item !== 'object') return null
  return firstPositiveInt(item.productId, item.product_id, item.product?.id)
}

/** Resolve item vs product ids from a GRV/NS source line (not a table row). */
function resolveLineIdsFromSourceItem(item) {
  const productId = resolveLineProductId(item)
  const itemId = resolveLineItemId(item)
  if (productId && !itemId) {
    return { itemId: null, productId, isProduct: true }
  }
  if (itemId) {
    return { itemId, productId: null, isProduct: false }
  }
  if (productId) {
    return { itemId: null, productId, isProduct: true }
  }
  return { itemId: null, productId: null, isProduct: false }
}

function resolveRowItemId(row) {
  if (!row) return null
  return firstPositiveInt(row.itemId)
}

function resolveRowProductId(row) {
  if (!row) return null
  return firstPositiveInt(row.productId)
}

function resolveVat(item) {
  const raw = item?.vat
  if (raw != null && Number.isFinite(Number(raw))) return Number(raw)
  return item?.isTaxable ? 15 : 0
}

function flattenGrvRecords(grvs) {
  const rows = []
  let no = 0

  for (const grv of grvs || []) {
    const supplierId = toIntId(grv?.supplier?.id ?? grv?.supplierId)
    const grvId = toIntId(grv?.id)
    const items = Array.isArray(grv?.items) ? grv.items : []
    for (const item of items) {
      const { itemId, productId, isProduct } = resolveLineIdsFromSourceItem(item)
      const categoryName = isProduct
        ? '-'
        : (item?.item?.category?.name || '-')
      const itemName = item?.item?.itemName || item?.product?.name || '-'
      const qtys = parseCsvNums(item?.quantityDetails)
      const prices = parseCsvNums(item?.unitPriceDetails)
      const vols = parseCsvNums(item?.variantDetails)
      const expiries = splitCsv(item?.expiryDateDetails)
      if (item?.expiryDate && expiries.length === 0) {
        expiries.push(formatExpiry(item.expiryDate))
      }

      const vat = resolveVat(item)
      const isTaxable = !!item?.isTaxable
      const lineRemark = String(item?.remark || grv?.remark || '').trim()

      if (qtys.length === 0) {
        no += 1
        const qty = Number(item?.quantity || 0)
        const price = Number(item?.unitPrice || 0)
        rows.push({
          key: `${grv.id}-${item.id}-0`,
          no,
          itemId,
          productId,
          itemCode: resolveItemCode(item),
          supplierId,
          grvId,
          grvItemId: toIntId(item?.id),
          grvNo: grv.grvNo || '-',
          prNo: item?.prNumber || '-',
          supplier: grv?.supplier?.name || '-',
          category: categoryName,
          item: itemName,
          isProduct,
          qty,
          volume: vols[0] ?? '-',
          unitPrice: price,
          totalPrice: Number(item?.totalPrice ?? qty * price),
          expiry: formatExpiry(item?.expiryDate),
          taxable: isTaxable ? 'Yes' : 'No',
          isTaxable,
          vat,
          remark: lineRemark,
          usePurchaseCategory: false,
          purchaseCategory: '',
          purchaseType: PURCHASE_TYPE_GOODS
        })
        continue
      }

      const maxLen = Math.max(qtys.length, prices.length, vols.length, expiries.length, 1)
      for (let i = 0; i < maxLen; i++) {
        no += 1
        const qty = Number(qtys[i] ?? qtys[0] ?? 0)
        const price = Number(prices[i] ?? prices[0] ?? item?.unitPrice ?? 0)
        const vol = vols[i] ?? vols[0]
        rows.push({
          key: `${grv.id}-${item.id}-${i}`,
          no,
          itemId,
          productId,
          itemCode: resolveItemCode(item),
          supplierId,
          grvId,
          grvItemId: toIntId(item?.id),
          grvNo: grv.grvNo || '-',
          prNo: item?.prNumber || '-',
          supplier: grv?.supplier?.name || '-',
          category: categoryName,
          item: itemName,
          isProduct,
          qty,
          volume: vol != null && vol !== '' ? vol : '-',
          unitPrice: price,
          totalPrice: qty * price,
          expiry: formatExpiry(expiries[i] ?? expiries[0] ?? item?.expiryDate),
          taxable: isTaxable ? 'Yes' : 'No',
          isTaxable,
          vat,
          remark: lineRemark,
          usePurchaseCategory: false,
          purchaseCategory: '',
          purchaseType: PURCHASE_TYPE_GOODS
        })
      }
    }
  }

  return rows
}

function flattenNsRecords(list) {
  const rows = []
  let no = 0

  for (const ns of list || []) {
    const supplierId = toIntId(ns?.supplier?.id ?? ns?.supplierId)
    const nsId = toIntId(ns?.id)
    const items = Array.isArray(ns?.items) ? ns.items : []
    for (const item of items) {
      const { itemId, productId, isProduct } = resolveLineIdsFromSourceItem(item)
      const itemName = item?.item?.itemName || item?.product?.name || '-'
      const categoryName = isProduct
        ? (ns?.category?.name || '-')
        : (item?.item?.category?.name || ns?.category?.name || '-')
      const deptName = ns?.department?.name || item?.product?.department?.name || '-'
      const qtys = parseCsvNums(item?.quantityDetails)
      const prices = parseCsvNums(item?.unitPriceDetails)
      const vols = parseCsvNums(item?.variantDetails)
      const vat = resolveVat(item)
      const isTaxable = !!item?.isTaxable
      const remark = normalizeRemarkForSave(item?.remark || ns?.remark)

      if (qtys.length === 0) {
        no += 1
        const qty = Number(item?.quantity || 0)
        const price = Number(item?.unitPrice || 0)
        rows.push({
          key: `${ns.id}-${item.id}-0`,
          no,
          itemId,
          productId,
          itemCode: resolveItemCode(item),
          supplierId,
          nsId,
          nsItemId: toIntId(item?.id),
          nsNo: ns.nsNo || '-',
          prNo: ns.prNo || '-',
          supplier: ns?.supplier?.name || '-',
          category: categoryName,
          dept: deptName,
          item: itemName,
          isProduct,
          qty,
          volume: vols[0] ?? '-',
          unitPrice: price,
          total: Number(item?.totalPrice ?? qty * price),
          taxable: isTaxable ? 'Yes' : 'No',
          isTaxable,
          remark,
          vat,
          usePurchaseCategory: false,
          purchaseCategory: '',
          purchaseType: PURCHASE_TYPE_GOODS
        })
        continue
      }

      const maxLen = Math.max(qtys.length, prices.length, vols.length, 1)
      for (let i = 0; i < maxLen; i++) {
        no += 1
        const qty = Number(qtys[i] ?? qtys[0] ?? 0)
        const price = Number(prices[i] ?? prices[0] ?? item?.unitPrice ?? 0)
        const vol = vols[i] ?? vols[0]
        rows.push({
          key: `${ns.id}-${item.id}-${i}`,
          no,
          itemId,
          productId,
          itemCode: resolveItemCode(item),
          supplierId,
          nsId,
          nsItemId: toIntId(item?.id),
          nsNo: ns.nsNo || '-',
          prNo: ns.prNo || '-',
          supplier: ns?.supplier?.name || '-',
          category: categoryName,
          dept: deptName,
          item: itemName,
          isProduct,
          qty,
          volume: vol != null && vol !== '' ? vol : '-',
          unitPrice: price,
          total: qty * price,
          taxable: isTaxable ? 'Yes' : 'No',
          isTaxable,
          remark,
          vat,
          usePurchaseCategory: false,
          purchaseCategory: '',
          purchaseType: PURCHASE_TYPE_GOODS
        })
      }
    }
  }

  return rows
}

function buildContextQuery(extra = {}) {
  const parts = []
  if (branchId.value) parts.push(`branchId=${encodeURIComponent(branchId.value)}`)
  else if (companyId.value) parts.push(`companyId=${encodeURIComponent(companyId.value)}`)
  else return null

  for (const [k, v] of Object.entries(extra)) {
    if (v != null && String(v).trim() !== '') {
      parts.push(`${k}=${encodeURIComponent(String(v).trim())}`)
    }
  }
  return parts.length ? `?${parts.join('&')}` : null
}

async function loadAvailableDocNumbers() {
  loadingAvailableGrvNumbers.value = true
  loadingAvailableNsNumbers.value = true
  try {
    const [grvRes, nsRes] = await Promise.all([
      apiFetch('/finance/payment-purchase/available-grv-numbers', { method: 'GET' }),
      apiFetch('/finance/payment-purchase/available-ns-numbers', { method: 'GET' })
    ])
    if (grvRes.ok) {
      const data = await grvRes.json().catch(() => [])
      availableGrvNumbers.value = Array.isArray(data) ? data : []
    } else {
      availableGrvNumbers.value = []
    }
    if (nsRes.ok) {
      const data = await nsRes.json().catch(() => [])
      availableNsNumbers.value = Array.isArray(data) ? data : []
    } else {
      availableNsNumbers.value = []
    }
  } catch {
    availableGrvNumbers.value = []
    availableNsNumbers.value = []
  } finally {
    loadingAvailableGrvNumbers.value = false
    loadingAvailableNsNumbers.value = false
  }
}

async function fetchGrvByNumber(grvNo) {
  const suffix = extractDocSuffix(grvNo)
  if (!suffix) return []

  const q = buildContextQuery({ grvNo: suffix })
  if (!q) {
    showToast('Company or branch context is required.', 'error')
    return []
  }
  const res = await apiFetch(`/grv/report${q}`, { method: 'GET' })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err?.message || 'Failed to load GRV data.')
  }
  const data = await res.json().catch(() => [])
  const list = Array.isArray(data) ? data : []
  return list.filter((g) => matchesDocNo(g?.grvNo, grvNo))
}

async function fetchNsByNumber(nsNo) {
  const suffix = extractDocSuffix(nsNo)
  if (!suffix) return []

  const q = buildContextQuery({ nsNo: suffix })
  if (!q) {
    showToast('Company or branch context is required.', 'error')
    return []
  }
  const res = await apiFetch(`/non-stockable/report${q}`, { method: 'GET' })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err?.message || 'Failed to load non-stockable data.')
  }
  const data = await res.json().catch(() => [])
  const list = Array.isArray(data) ? data : []
  return list.filter((n) => matchesDocNo(n?.nsNo, nsNo))
}

/** P.C.P.V: pick which document to generate when toolbar fields are not cleared between adds. */
function resolvePcpvGenerateTarget(grvNo, nsNo) {
  if (otherModeActive.value) return 'other'
  const grvNew = grvNo && !pcpvQueueHasDoc('grv', grvNo, null)
  const nsNew = nsNo && !pcpvQueueHasDoc('ns', null, nsNo)
  if (grvNew && nsNew) {
    return tableMode.value === 'grv' || tableMode.value === 'other' ? 'ns' : 'grv'
  }
  if (grvNew) return 'grv'
  if (nsNew) return 'ns'
  if (grvNo) return 'grv'
  if (nsNo) return 'ns'
  return null
}

async function onGenerate() {
  const grvNo = String(draft.grvNumber || '').trim()
  const nsNo = String(draft.nonStockableNumber || '').trim()
  const paymentType = String(draft.paymentType || '').trim()

  if (!paymentType) {
    showToast('Please select a payment type.', 'error')
    return
  }

  if (!otherModeActive.value && !grvNo && !nsNo) {
    showToast('Select a GRV Number, Non-Stockable Number, or choose Other.', 'error')
    return
  }

  const pcpvSessionContinuing = isPcpvPaymentType() && (pcpvQueue.value.length > 0 || !!tableMode.value)
  if (pcpvSessionContinuing) {
    const committed = commitCurrentTableToPcpvQueue()
    if (!committed) return
  }

  clearReceiptAndDetailFields()

  isLoading.value = true

  if (!isPcpvPaymentType()) {
    grvRows.value = []
    nsRows.value = []
    otherRows.value = []
    grvExtraRows.value = []
    nsExtraRows.value = []
    otherExtraRows.value = []
    tableMode.value = null
    cpvVoucherNo.value = ''
    if (!otherModeActive.value) {
      savedPaymentId.value = null
      clearCpvPreparedBy()
    }
  } else {
    pcpvLoadedBatch.value = null
    if (!pcpvSessionContinuing) {
      pcpvQueue.value = []
      pcpvSelectedAddedDocKey.value = ''
      clearCpvPreparedBy()
      tableMode.value = null
      grvRows.value = []
      nsRows.value = []
      otherRows.value = []
      grvExtraRows.value = []
      nsExtraRows.value = []
      otherExtraRows.value = []
    }
  }

  try {
    await loadCpvActiveBanks()

    const pcpvTarget = isPcpvPaymentType() ? resolvePcpvGenerateTarget(grvNo, nsNo) : null

    if (otherModeActive.value || pcpvTarget === 'other') {
      const saved = savedPaymentId.value
        ? await fetchSavedPaymentOther(savedPaymentId.value)
        : null
      tableMode.value = 'other'
      if (isPcpvPaymentType()) {
        pcpvActiveOtherDocKey.value = nextPcpvOtherDocKey()
      }
      otherRows.value = []
      if (shouldLoadSavedCpvDocument(saved)) {
        await applySavedRecord(saved)
        showToast('Loaded saved Other payment purchase.', 'success')
      } else {
        otherExtraRows.value = [createEmptyOtherRow()]
        if (isSingleVoucherPaymentType() || isPcpvPaymentType()) {
          if (!cpvVoucherNo.value) cpvVoucherNo.value = await fetchNextCpvNumber()
          if (!cpvPreparedByUserId.value) initCpvPreparedByForNew()
        }
        showToast('Other entry ready. Add line details and save.', 'success')
      }
    } else if (grvNo && (!isPcpvPaymentType() || pcpvTarget === 'grv')) {
      const saved = await fetchSavedPayment(grvNo, null)
      if (saved?.isPcpvBatch) {
        applyPcpvBatchLoad(saved)
        showToast('Loaded saved P.C.P.V batch.', 'success')
        return
      }
      if (isPcpvPaymentType() && saved?.id) {
        showToast('This GRV is already saved under C.P.V. Open it as C.P.V or use a new number.', 'error')
        return
      }
      const grvs = await fetchGrvByNumber(grvNo)
      if (!saved && (!grvs || grvs.length === 0)) {
        showToast(`No GRV found for "${grvNo}".`, 'error')
        return
      }
      if (isPcpvPaymentType() && pcpvQueueHasDoc('grv', grvNo, null)) {
        showToast('This GRV is already in the P.C.P.V list.', 'error')
        return
      }
      if (shouldLoadSavedCpvDocument(saved)) {
        tableMode.value = 'grv'
        await applySavedRecord(saved)
        showToast('Loaded saved payment purchase.', 'success')
      } else {
        const newGrvRows = flattenGrvRecords(grvs)
        assignPcpvGrvRows(newGrvRows)
        const fullGrvNo = resolveFullGrvNumber()
        if (fullGrvNo) draft.grvNumber = fullGrvNo
        if (isSingleVoucherPaymentType() || isPcpvPaymentType()) {
          if (!cpvVoucherNo.value) cpvVoucherNo.value = await fetchNextCpvNumber()
          if (!cpvPreparedByUserId.value) initCpvPreparedByForNew()
        }
        showToast(`Loaded ${grvRows.value.length} GRV line(s).`, 'success')
      }
    } else {
      const saved = await fetchSavedPayment(null, nsNo)
      if (saved?.isPcpvBatch) {
        applyPcpvBatchLoad(saved)
        showToast('Loaded saved P.C.P.V batch.', 'success')
        return
      }
      if (isPcpvPaymentType() && saved?.id) {
        showToast('This non-stockable is already saved under C.P.V.', 'error')
        return
      }
      const list = await fetchNsByNumber(nsNo)
      if (!saved && (!list || list.length === 0)) {
        showToast(`No non-stockable found for "${nsNo}".`, 'error')
        return
      }
      if (isPcpvPaymentType() && pcpvQueueHasDoc('ns', null, nsNo)) {
        showToast('This non-stockable is already in the P.C.P.V list.', 'error')
        return
      }
      if (shouldLoadSavedCpvDocument(saved)) {
        tableMode.value = 'ns'
        await applySavedRecord(saved)
        showToast('Loaded saved payment purchase.', 'success')
      } else {
        const newNsRows = flattenNsRecords(list)
        assignPcpvNsRows(newNsRows)
        const fullNsNo = resolveFullNsNumber()
        if (fullNsNo) draft.nonStockableNumber = fullNsNo
        if (isSingleVoucherPaymentType() || isPcpvPaymentType()) {
          if (!cpvVoucherNo.value) cpvVoucherNo.value = await fetchNextCpvNumber()
          if (!cpvPreparedByUserId.value) initCpvPreparedByForNew()
        }
        showToast(`Loaded ${nsRows.value.length} non-stockable line(s).`, 'success')
      }
    }
  } catch (e) {
    showToast(e?.message || 'Failed to generate list.', 'error')
    tableMode.value = null
  } finally {
    isLoading.value = false
  }
}

watch(
  () => draft.paymentType,
  (next, prev) => {
    if (next === prev) return
    resetPcpvSession()
    if (!isPcpvPaymentType()) {
      pcpvLoadedBatch.value = null
    }
    if (next === PAYMENT_TYPE_DIRECT) {
      draft.sellerTin = ''
      draft.sellerName = ''
      draft.receiptNumber = ''
      draft.receiptDate = ''
      draft.mrc = ''
    }
  }
)

watch(
  () => draft.purchaseCategory,
  (next) => {
    if (!String(next || '').trim()) {
      clearAllRowPurchaseCategoryFlags()
    }
  }
)
</script>

<style scoped>
.payment-purchase-form {
  min-width: 0;
}

.payment-purchase-toolbar {
  display: grid;
  /* GRV | NS | Other | C.P.V/P.C.P.V | Generate — inputs equal (1fr), buttons equal (0.72fr) */
  grid-template-columns:
    minmax(0, 1fr)
    minmax(0, 1fr)
    minmax(0, 0.72fr)
    minmax(0, 1fr)
    minmax(0, 0.72fr);
  align-items: end;
  gap: 0.75rem 1rem;
  width: 100%;
  min-width: 0;
}

.payment-purchase-toolbar__field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.payment-purchase-toolbar__label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #1e293b;
  text-align: left;
  white-space: nowrap;
  line-height: 1.2;
}

.payment-purchase-toolbar__label--spacer {
  visibility: hidden;
  user-select: none;
}

.payment-purchase-toolbar__control {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 0.375rem 0.625rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  outline: none;
  font-size: 0.875rem;
  line-height: 1.25;
  background: #fff;
  transition: border-color 0.15s ease;
}

.payment-purchase-toolbar__control:focus {
  border-color: #0d4169;
}

.payment-purchase-toolbar__control:disabled {
  background: #f1f5f9;
  color: #64748b;
  cursor: not-allowed;
}

.payment-purchase-toolbar__control--select {
  cursor: pointer;
  padding-right: 1.75rem;
}

.payment-purchase-toolbar__control--select:disabled {
  cursor: not-allowed;
}

.payment-purchase-toolbar__btn {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 0.375rem 0.625rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.25;
  white-space: nowrap;
  text-align: center;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.1s ease;
}

.payment-purchase-toolbar__btn--other {
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
  font-weight: 600;
}

.payment-purchase-toolbar__btn--other:hover:not(.payment-purchase-toolbar__btn--other-active) {
  border-color: #0d4169;
}

.payment-purchase-toolbar__btn--other-active {
  border-color: #0d4169;
  background: #0d4169;
  color: #fff;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

.payment-purchase-toolbar__btn--generate {
  border: 1px solid #0d4169;
  color: #fff;
  background: #0d4169;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

.payment-purchase-toolbar__btn--generate:hover:not(:disabled) {
  background: #0a3354;
  border-color: #0a3354;
}

.payment-purchase-toolbar__btn--generate:active:not(:disabled) {
  transform: scale(0.95);
}

.payment-purchase-toolbar__btn--generate:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.payroll-table.payment-purchase-table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.68rem;
  color: #1e293b;
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
}

.payment-purchase-table th,
.payment-purchase-table td {
  padding: 2px 5px;
  border: none;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
  font-size: 0.66rem;
  line-height: 1.15;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  box-sizing: border-box;
}

.payment-purchase-table th {
  overflow: hidden;
  text-overflow: ellipsis;
}

.payment-purchase-table thead th {
  position: sticky;
  top: 0;
  z-index: 20;
  background: #f1f5f9;
  font-weight: 600;
  color: #1e293b;
  text-align: left;
  box-shadow: 0 1px 0 #e2e8f0;
}

.payment-purchase-table tbody td {
  text-align: left;
}

.payment-purchase-table tbody tr:hover {
  background: #f8fafc;
}

.payment-purchase-table .col-idx,
.payment-purchase-table .col-taxable {
  text-align: center;
}

/* Manual / dropdown cells: allow controls without blowing column width */
.payment-purchase-table td.col-category,
.payment-purchase-table td.col-item,
.payment-purchase-table td.col-purchase-type,
.payment-purchase-table td.col-qty,
.payment-purchase-table td.col-price,
.payment-purchase-table td.col-remark,
.payment-purchase-table td.col-taxable {
  overflow: visible;
  text-overflow: clip;
}

.payment-purchase-table th.num,
.payment-purchase-table td.num {
  text-align: right;
}

/* Dense column widths — shared by GRV / NS / Other tables */
.payment-purchase-table .col-idx {
  width: 1.6rem;
}

.payment-purchase-table .col-code {
  width: 4.75rem;
}

.payment-purchase-table .col-doc {
  width: 4.75rem;
}

.payment-purchase-table .col-pr {
  width: 4.75rem;
}

.payment-purchase-table .col-supplier {
  width: 6.5rem;
}

.payment-purchase-table .col-category {
  width: 5.5rem;
}

.payment-purchase-table .col-dept {
  width: 5.5rem;
}

.payment-purchase-table .col-item {
  width: 7.5rem;
}

.payment-purchase-table .col-purchase-cat {
  width: 6.75rem;
}

.payment-purchase-table .col-purchase-type {
  width: 5.5rem;
}

.payment-purchase-table .col-vol {
  width: 4.75rem;
}

.payment-purchase-table .col-qty {
  width: 4.75rem;
}

.payment-purchase-table .col-price {
  width: 4.75rem;
}

.payment-purchase-table .col-total {
  width: 4.75rem;
}

.payment-purchase-table .col-expiry {
  width: 4.75rem;
}

.payment-purchase-table .col-taxable {
  width: 4.25rem;
}

.payment-purchase-table .col-remark {
  width: 4.75rem;
}

.payment-purchase-table col.col-idx { width: 1.6rem; }
.payment-purchase-table col.col-code { width: 4.75rem; }
.payment-purchase-table col.col-doc { width: 4.75rem; }
.payment-purchase-table col.col-pr { width: 4.75rem; }
.payment-purchase-table col.col-supplier { width: 6.5rem; }
.payment-purchase-table col.col-category { width: 5.5rem; }
.payment-purchase-table col.col-dept { width: 5.5rem; }
.payment-purchase-table col.col-item { width: 7.5rem; }
.payment-purchase-table col.col-purchase-cat { width: 6.75rem; }
.payment-purchase-table col.col-purchase-type { width: 5.5rem; }
.payment-purchase-table col.col-vol { width: 4.75rem; }
.payment-purchase-table col.col-qty { width: 4.75rem; }
.payment-purchase-table col.col-price { width: 4.75rem; }
.payment-purchase-table col.col-total { width: 4.75rem; }
.payment-purchase-table col.col-expiry { width: 4.75rem; }
.payment-purchase-table col.col-taxable { width: 4.25rem; }
.payment-purchase-table col.col-remark { width: 4.75rem; }

.payment-purchase-table thead th.col-vol,
.payment-purchase-table thead th.col-qty,
.payment-purchase-table thead th.col-price,
.payment-purchase-table thead th.col-total,
.payment-purchase-table thead th.col-expiry,
.payment-purchase-table tbody td.col-vol,
.payment-purchase-table tbody td.col-qty,
.payment-purchase-table tbody td.col-price,
.payment-purchase-table tbody td.col-total,
.payment-purchase-table tbody td.col-expiry,
.payment-purchase-table tfoot td.totals-amount-block .totals-value {
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.payment-purchase-table thead th.col-price {
  white-space: normal;
  word-break: break-word;
  hyphens: auto;
}

.payment-purchase-table tfoot td.totals-amount-block {
  vertical-align: top;
  overflow: visible;
  white-space: normal;
  padding: 2px 5px;
  font-variant-numeric: tabular-nums;
}

.totals-amount-grid {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  width: 100%;
  box-sizing: border-box;
}

/* Align with Qty | Unit Price | Total Price — value in the third column */
.totals-amount-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: center;
  column-gap: 0;
  min-height: 1.28rem;
  width: 100%;
  box-sizing: border-box;
}

.totals-amount-row .totals-label {
  grid-column: 1 / 3;
  text-align: right;
  justify-self: end;
  padding-right: 0;
}

.totals-amount-row .totals-value {
  grid-column: 3;
  text-align: center;
  justify-self: stretch;
  width: 100%;
  box-sizing: border-box;
}

.totals-amount-row--grand {
  border-bottom: 1px solid #cbd5e1;
  padding-top: 0.25rem;
  margin-top: 0.05rem;
}

.totals-amount-row--grand .totals-label,
.totals-amount-row--grand .totals-value {
  color: #0d4169;
  font-weight: 800;
}

.payment-purchase-table tfoot.totals-footer-row td,
.payment-purchase-table tr.totals-footer-row td {
  background: #f1f5f9;
  border-top: 2px solid #cbd5e1;
  vertical-align: top;
}


.totals-label {
  font-weight: 600;
  font-size: 0.65rem;
  color: #475569;
  line-height: 1.2;
  text-align: right;
  white-space: nowrap;
}

.totals-value {
  font-weight: 700;
  font-size: 0.66rem;
  color: #1e293b;
  line-height: 1.15;
  text-align: center;
}

.status-pill {
  display: inline-block;
  padding: 1px 5px;
  border-radius: 9999px;
  font-weight: 700;
  font-size: 0.58rem;
  border: 1px solid transparent;
  line-height: 1.2;
}

.status-pill.active {
  background: #ecfdf5;
  color: #047857;
  border-color: #a7f3d0;
}

.status-pill.inactive {
  background: #fff1f2;
  color: #be123c;
  border-color: #fecdd3;
}

.receipt-meta-row {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  align-items: end;
  gap: 0.5rem 0.75rem;
  width: 100%;
  min-width: 0;
}

.receipt-meta-row .receipt-field {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}

.receipt-meta-row label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.1;
}

.receipt-meta-row input,
.receipt-meta-row select {
  padding: 0.2rem 0.45rem !important;
  font-size: 0.75rem !important;
  line-height: 1.15;
  min-height: 1.45rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  outline: none;
  background: #fff;
  transition: border-color 0.15s ease;
}

.receipt-meta-row input:focus,
.receipt-meta-row select:focus {
  border-color: #0d4169;
}

.purchase-category-row {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 1rem;
  width: 100%;
}

.purchase-category-field {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  width: 100%;
  max-width: 22rem;
  min-width: 14rem;
}

.purchase-category-field label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.1;
}

.purchase-category-field select {
  padding: 0.2rem 0.45rem !important;
  font-size: 0.75rem !important;
  line-height: 1.15;
  min-height: 1.45rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  outline: none;
  background: #fff;
}

.purchase-category-field select:focus {
  border-color: #0d4169;
}

.purchase-cat-cell {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-wrap: nowrap;
  flex-direction: row;
  font-size: 0.72rem;
  line-height: 1.2;
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.purchase-cat-checkbox {
  width: 0.9rem;
  height: 0.9rem;
  flex: 0 0 auto;
  margin: 0;
  accent-color: #0d4169;
  cursor: pointer;
}

.taxable-checkbox {
  width: 0.75rem;
  height: 0.75rem;
  margin: 0 auto;
  display: block;
  accent-color: #0d4169;
  cursor: pointer;
}

.purchase-cat-checkbox:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.purchase-cat-label {
  color: #334155;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 1 auto;
  min-width: 0;
  line-height: 1.2;
}

.payment-purchase-table col.col-purchase-cat {
  width: 6.75rem;
}

.payment-purchase-table th.col-purchase-cat,
.payment-purchase-table td.col-purchase-cat {
  white-space: nowrap;
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: 2px;
}

.payment-purchase-table th.col-purchase-type,
.payment-purchase-table td.col-purchase-type {
  white-space: nowrap;
  vertical-align: middle;
  overflow: visible;
  padding-left: 2px;
  padding-right: 2px;
}

.purchase-type-select {
  width: 100%;
  min-width: 4.75rem;
  max-width: 5.5rem;
}

.purchase-type-locked {
  display: inline-block;
  width: 100%;
  color: #334155;
}

.payment-purchase-table th.col-vol,
.payment-purchase-table td.col-vol {
  padding-left: 2px;
}

.add-row-tr:hover {
  background: #f8fafc;
}

.col-idx-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  width: 100%;
  max-width: 100%;
}

.col-idx-num {
  min-width: 0.65rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.remove-row-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15rem;
  height: 1.15rem;
  padding: 0;
  border: 1px solid #fecaca;
  border-radius: 0.25rem;
  background: #fff;
  color: #b91c1c;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}

.remove-row-btn:hover {
  background: #fef2f2;
  border-color: #f87171;
}

.add-row-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.35rem;
  height: 1.35rem;
  padding: 0;
  border: 1px solid #cbd5e1;
  border-radius: 0.25rem;
  background: #fff;
  color: #0d4169;
  font-size: 0.85rem;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.add-row-btn:hover {
  background: #f1f5f9;
  border-color: #0d4169;
}

.manual-row td {
  color: #1e293b;
}

.payment-purchase-table .manual-row td.col-idx {
  overflow: visible;
}

.payment-purchase-table .col-code {
  text-align: left;
}

.manual-cell-select,
.manual-cell-input {
  padding: 0.15rem 0.35rem;
  font-size: 0.66rem;
  line-height: 1.15;
  border: 1px solid #cbd5e1;
  border-radius: 0.25rem;
  background: #fff;
  color: #1e293b;
  outline: none;
  box-sizing: border-box;
}

.manual-cell-input {
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.manual-account-select {
  display: block;
  width: auto;
  min-width: 3rem;
  max-width: 5rem;
}

.manual-cell-select:focus,
.manual-cell-input:focus {
  border-color: #0d4169;
}

.manual-item-stack {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  width: auto;
  min-width: 0;
  max-width: 100%;
  align-items: flex-start;
}

.payment-purchase-table .manual-row td.col-category,
.payment-purchase-table .manual-row td.col-item {
  white-space: nowrap;
}

.payment-purchase-table .manual-row td.col-item .remark-table-field {
  white-space: normal;
}

.manual-cell-input-narrow {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  padding-left: 0.2rem;
  padding-right: 0.2rem;
}

.payment-purchase-table .manual-row td.col-qty .manual-cell-input-narrow,
.payment-purchase-table .manual-row td.col-price .manual-cell-input-narrow {
  display: block;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  margin: 0;
  box-sizing: border-box;
}

.payment-table-wrap {
  position: relative;
  overflow: visible;
}

/* Vertical + horizontal scroll inside table area (sticky thead like consumptionPage). */
.payment-table-scroll {
  max-height: var(--payment-table-scroll-max-height, min(32rem, calc(100vh - 14rem)));
  overflow: auto;
  width: 100%;
  -webkit-overflow-scrolling: touch;
}

.payment-table-h-scroll__inner {
  display: inline-block;
  min-width: 100%;
  vertical-align: top;
}

.payment-table-wrap--cpv {
  padding-bottom: 0.5rem;
}

.payment-table-wrap--cpv .payment-table-scroll {
  max-height: var(--payment-table-scroll-max-height-cpv, min(40rem, calc(100vh - 12rem)));
}

.payment-table-scroll.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.payment-table-scroll.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.payment-table-scroll.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.payment-table-scroll.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.payment-purchase-table tfoot.totals-footer-row td {
  overflow: visible;
}

.payment-purchase-table tfoot td.footer-cpv-anchor {
  --cpv-form-width: 100%;
  position: relative;
  overflow: visible;
  vertical-align: top;
  white-space: normal;
  text-align: left;
  padding-left: 2px;
  padding-right: 2px;
}

.payment-purchase-table tfoot.totals-footer-row td.footer-cpv-anchor {
  border-top: 2px solid #cbd5e1;
  background: #f1f5f9;
}

.footer-cpv-panel {
  position: relative;
  display: block;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  margin-top: 0.25rem;
  box-sizing: border-box;
  z-index: 5;
  text-align: left;
}

.footer-cpv-panel :deep(.cheque-payment-voucher) {
  --cpv-form-width: 100%;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

/* P.C.P.V: same left-aligned footprint as C.P.V table-footer CPV cell */
.pcpv-cpv-panel {
  width: 100%;
  max-width: var(--cpv-footer-max-width);
  margin-right: auto;
  text-align: left;
}

.pcpv-cpv-panel :deep(.cheque-payment-voucher) {
  --cpv-form-width: 100%;
  width: 100%;
  min-width: 0;
  max-width: var(--cpv-footer-max-width);
  box-sizing: border-box;
}

.doc-toggle-btn {
  text-align: center;
  cursor: pointer;
}

.doc-toggle-btn--active {
  box-shadow: inset 0 0 0 1px #0d4169;
}

.remark-table-field {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  padding: 1px 3px;
  border: 1px solid transparent;
  border-radius: 3px;
  outline: none;
  background-color: #fef2f2;
  font-size: 0.66rem;
  text-align: center;
  font-variant-numeric: tabular-nums;
  transition: all 0.15s;
}

.remark-table-field:focus {
  border-color: #0d4169;
  background-color: #fff;
}

/* ——— Responsive (phone / tablet) ——— */
.payment-purchase-page {
  min-width: 0;
  max-width: 100%;
  /* GRV table footer CPV column span (first 8 columns). */
  --cpv-footer-max-width: 45rem;
  --payment-table-scroll-max-height: min(32rem, calc(100vh - 14rem));
  --payment-table-scroll-max-height-cpv: min(40rem, calc(100vh - 12rem));
}

.payment-purchase-section-pad {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

.payment-purchase-card-scroll {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.payment-purchase-summary-table {
  min-width: 16rem;
}

.payment-table-scroll .payment-table-h-scroll__inner .payment-purchase-table {
  min-width: 52rem;
}

.payment-purchase-table-footer {
  background: #f1f5f9;
  border-top: 2px solid #cbd5e1;
  padding: 0.75rem 0.75rem 0.5rem;
  margin-top: 0.5rem;
  overflow: visible;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.payment-purchase-table-footer .footer-cpv-panel {
  flex: 0 1 calc(100% - 22.75rem);
  width: calc(100% - 22.75rem);
  max-width: calc(100% - 22.75rem);
  margin-top: 0;
}

.payment-purchase-table-footer .totals-amount-block {
  flex: 0 0 22rem;
  max-width: 22rem;
  margin-left: auto;
}

@media (max-width: 900px) {
  .payment-purchase-table-footer {
    flex-direction: column;
  }

  .payment-purchase-table-footer .footer-cpv-panel,
  .payment-purchase-table-footer .totals-amount-block {
    flex: 1 1 auto;
    max-width: 100%;
    margin-left: 0;
  }
}

.payment-purchase-toolbar__control,
.payment-purchase-toolbar__btn,
.payment-purchase-primary-btn {
  min-height: 2.5rem;
}

@media (max-width: 1023px) {
  .payment-purchase-toolbar {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .payment-purchase-toolbar__field--generate {
    grid-column: 1 / -1;
  }

  .receipt-meta-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 639px) {
  .payment-purchase-section-pad {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }

  .payment-purchase-toolbar {
    grid-template-columns: 1fr;
    gap: 0.65rem;
  }

  .payment-purchase-toolbar__label--spacer {
    display: none;
  }

  .payment-purchase-toolbar__label {
    white-space: normal;
  }

  .receipt-meta-row {
    grid-template-columns: 1fr;
    gap: 0.65rem;
  }

  .receipt-meta-row input,
  .receipt-meta-row select {
    min-height: 2.5rem !important;
    font-size: 1rem !important;
  }

  .payment-purchase-toolbar__control {
    font-size: 1rem;
    min-height: 2.5rem;
  }

  .payment-purchase-batch-meta {
    grid-template-columns: 1fr;
    max-width: none;
  }

  .payment-purchase-actions {
    width: 100%;
    padding-left: 0;
    padding-right: 0;
  }

  .payment-purchase-actions .payment-purchase-primary-btn {
    width: 100%;
    min-width: 0;
    min-height: 2.75rem;
  }

  /* Stack footer: CPV on top, amounts below */
  .payment-purchase-table tfoot.totals-footer-row {
    display: block;
  }

  .payment-purchase-table tfoot.totals-footer-row td {
    display: block;
    width: 100%;
    box-sizing: border-box;
    border-top: none;
    white-space: normal;
  }

  .payment-purchase-table tfoot.totals-footer-row td.footer-cpv-anchor {
    border-top: 2px solid #cbd5e1;
    padding-bottom: 0.75rem;
  }

  .payment-purchase-table tfoot.totals-footer-row td.totals-amount-block {
    border-top: 1px solid #e2e8f0;
    padding-top: 0.5rem;
  }

  .payment-purchase-table tfoot.totals-footer-row td.col-expiry,
  .payment-purchase-table tfoot.totals-footer-row td.col-taxable,
  .payment-purchase-table tfoot.totals-footer-row td.col-remark {
    display: none;
  }

  .totals-amount-row {
    grid-template-columns: 1fr minmax(5.5rem, auto);
  }

  .totals-amount-row .totals-label {
    grid-column: 1;
    text-align: right;
  }

  .totals-amount-row .totals-value {
    grid-column: 2;
  }

  .manual-account-select {
    max-width: 100%;
    width: 100%;
  }

  .payment-purchase-table .manual-row td.col-item {
    white-space: normal;
  }

  .remark-table-field {
    min-height: 2rem;
    font-size: 0.75rem;
  }
}

</style>
